import React from 'react';
import AppLink from './AppLink';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import SEO from './SEO';
import { useLanguage } from '../i18n/LanguageContext';
import { ArrowRight } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type BadgeColor = 'amber' | 'lapis' | 'ventral' | 'crise' | 'taupe';

interface ChangeItem {
  type: '+' | '~' | '-' | 'x';
  text: string;
  detail?: string; // contenu technique repliable
}

interface ChangelogEntryProps {
  version: string;
  date: string;
  badge: string;
  badgeColor?: BadgeColor;
  title: string;
  items: ChangeItem[];
}

// ─── Composant ChangelogEntry ─────────────────────────────────────────────────

function ChangelogEntry({
  version,
  date,
  badge,
  badgeColor = 'amber',
  title,
  items,
}: ChangelogEntryProps) {
  const [open, setOpen] = React.useState(false);

  const badgeStyles: Record<BadgeColor, string> = {
    amber:   'bg-amber/10 text-amber',
    lapis:   'bg-lapis/10 text-lapis',
    ventral: 'bg-ventral/10 text-ventral',
    crise:   'bg-crise/10 text-crise',
    taupe:   'bg-sand/40 text-taupe',
  };

  const typeColor: Record<ChangeItem['type'], string> = {
    '+': 'text-ventral',
    '~': 'text-amber',
    '-': 'text-crise',
    'x': 'text-crise',
  };

  const typeLabel: Record<ChangeItem['type'], string> = {
    '+': 'Nouveau',
    '~': 'Amélioré',
    '-': 'Retiré',
    'x': 'Corrigé',
  };

  const publicItems  = items.filter(i => !i.detail);
  const technicalItems = items.filter(i => i.detail);

  return (
    <article className="border-b border-sand/60 pb-10 last:border-0">
      {/* En-tête */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="font-sans text-[12px] font-semibold tracking-[0.06em] uppercase text-taupe">
          {version}
        </span>
        <span className="text-[12px] text-taupe/70">{date}</span>
        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] ${badgeStyles[badgeColor]}`}>
          {badge}
        </span>
      </div>

      {/* Titre */}
      <h2 className="font-serif text-[22px] md:text-[26px] font-light text-ink mb-5 leading-[1.25]">
        {title}
      </h2>

      {/* Items publics */}
      <ul className="space-y-3">
        {publicItems.map((item, i) => (
          <li key={i} className="flex gap-3 text-[14px] text-ink/75 leading-relaxed">
            <span className={`shrink-0 text-[11px] font-semibold uppercase tracking-[0.05em] mt-[3px] ${typeColor[item.type]}`}>
              {typeLabel[item.type]}
            </span>
            <span>{item.text}</span>
          </li>
        ))}
      </ul>

      {/* Détails techniques repliables */}
      {technicalItems.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setOpen(v => !v)}
            className="text-[12px] text-taupe hover:text-ink transition-colors flex items-center gap-1.5 font-medium"
            aria-expanded={open}
          >
            <span className={`inline-block transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>›</span>
            {open ? 'Masquer les détails techniques' : 'Voir les détails techniques'}
          </button>
          {open && (
            <ul className="mt-3 space-y-2 pl-4 border-l border-sand">
              {technicalItems.map((item, i) => (
                <li key={i} className="text-[13px] text-taupe leading-relaxed font-mono">
                  {item.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Données ──────────────────────────────────────────────────────────────────

const ENTRIES: ChangelogEntryProps[] = [
  {
    version: 'v1.78.0',
    date: '14 octobre 2026',
    badge: 'Design',
    badgeColor: 'amber',
    title: 'Landing première séance : rythme vertical, situations centrées, photo collante',
    items: [
      { type: '~', text: 'Intro du hero raccourcie, sans répéter Rixensart.' },
      { type: '~', text: '« Cochez ce qui vous ressemble » de nouveau centré ; photo du praticien collante en bureau.' },
      { type: '~', text: 'Espacements verticaux harmonisés entre surtitres, titres, textes, témoignages et boutons.' },
      { type: '~', text: 'Vignette mobile de « Prendre contact » recadrée sur le visage.' },
    ],
  },
  {
    version: 'v1.77.0',
    date: '14 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : nouveau portrait dans « Prendre contact »',
    items: [
      { type: '~', text: 'Portrait en lumière naturelle, regard direct, à côté du formulaire.' },
    ],
  },
  {
    version: 'v1.76.0',
    date: '14 octobre 2026',
    badge: 'Design',
    badgeColor: 'amber',
    title: 'Landing première séance : grille de lecture commune et nouvelles photos',
    items: [
      { type: '~', text: 'Même conteneur et même bord gauche pour toutes les sections, pied de page compris.' },
      { type: '~', text: '« Pourquoi ? » et « Comment ça marche ? » en deux colonnes sur ordinateur.' },
      { type: '~', text: 'Largeur de lecture homogène pour les paragraphes et les témoignages ; boutons alignés partout.' },
      { type: '~', text: 'Photo au cabinet dans « Votre praticien », portrait en vignette dans « Prendre contact ».' },
    ],
  },
  {
    version: 'v1.75.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : photos de Bertrand inversées',
    items: [
      { type: '~', text: 'Portrait en intérieur dans « Votre praticien », photo au cabinet dans « Prendre contact ».' },
    ],
  },
  {
    version: 'v1.74.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : portrait de Bertrand près du formulaire',
    items: [
      { type: '+', text: 'Portrait en intérieur, regard direct, et légende « Praticien en régulation somatique » dans « Prendre contact ».' },
    ],
  },
  {
    version: 'v1.73.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : témoignages harmonisés',
    items: [
      { type: '~', text: 'Tous les témoignages de section avec étoiles et portrait aquarelle (quatre nouveaux portraits).' },
      { type: '~', text: 'Étoiles ajoutées aux cartes « Retours d\'expérience ».' },
      { type: '~', text: 'Socle de formation : Maître Praticien PNL, 2026.' },
      { type: '~', text: 'Bouton fixe mobile visible dès le début de « Pourquoi ? ».' },
    ],
  },
  {
    version: 'v1.72.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : section Bertrand remontée après « Pourquoi ? »',
    items: [
      { type: '~', text: 'Surtitre « Votre praticien », nouvelle photo au cabinet, lien « Parcours complet » retiré.' },
      { type: '~', text: 'Bouton aligné sur celui de « Pourquoi ? ».' },
      { type: '~', text: 'Rythme de « Pourquoi ? » repris (strophe, phrase-pivot, encadré, aparté) et témoignage de Marc avec portrait.' },
    ],
  },
  {
    version: 'v1.71.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : preuve sociale sous le bouton du hero',
    items: [
      { type: '~', text: '« 200+ personnes accompagnées » remplace « Appel de 15 min · Sans engagement » sous le bouton du hero.' },
    ],
  },
  {
    version: 'v1.70.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : nouveau titre du hero',
    items: [
      { type: '~', text: 'H1 « Quand la tension ne redescend plus » et sous-titre centré sur un système nerveux apaisé.' },
    ],
  },
  {
    version: 'v1.69.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : fin de la section « Pourquoi ? » rythmée',
    items: [
      { type: '~', text: 'Proposition en encadré, « Juste, enfin, souffler. » en point d\'orgue, « De vous à moi » en aparté.' },
      { type: '~', text: 'Accroche du témoignage d\'Isabelle raccourcie à « très apaisée ».' },
    ],
  },
  {
    version: 'v1.68.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : rythme visuel et bouton fixe mobile',
    items: [
      { type: '+', text: 'Bouton fixe « Demander un appel gratuit » sur mobile, affiché après la section « Pourquoi ? » et masqué quand un autre bouton est visible.' },
      { type: '~', text: 'Section « Pourquoi ? » : trois phrases-pivots, deux surlignages, symptômes en liste, deux phrases raccourcies.' },
      { type: '~', text: 'Corps de texte plus foncé, 17px sur mobile ; pied de page sans défilement horizontal à 768px.' },
    ],
  },
  {
    version: 'v1.67.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : témoignage d\'Isabelle et appel à l\'action harmonisés',
    items: [
      { type: '~', text: 'Titre de « Comprendre ne suffit parfois pas » aligné sur les autres sections.' },
      { type: '~', text: 'Témoignage d\'Isabelle au format de celui de Virginie (étoiles, portrait sous la citation).' },
      { type: '~', text: 'Bouton centré avec « Appel de 15 min · Sans engagement », comme dans les situations.' },
      { type: '~', text: 'Conclusion des situations toujours en Nocturne, seule la pastille reste en argile.' },
    ],
  },
  {
    version: 'v1.66.2',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : phrase sous le titre du hero ajustée',
    items: [
      { type: '~', text: 'Hero : « Accompagner votre corps à relâcher les tensions accumulées, retrouver son énergie et apaiser la surcharge mentale. »' },
    ],
  },
  {
    version: 'v1.66.1',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : phrase sous le titre du hero',
    items: [
      { type: '~', text: 'Hero : « Ensemble et à votre rythme, nous permettons à votre corps de quitter le mode « survie » pour réapprendre, étape par étape, à se sentir en sécurité. »' },
    ],
  },
  {
    version: 'v1.66.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : refonte de « Comprendre ne suffit parfois pas »',
    items: [
      { type: '~', text: 'Texte réécrit en phrases courtes, strophes compactes et deux phrases fortes, colonne de 680px sur ordinateur.' },
      { type: '~', text: '« De vous à moi » intégré au fil du texte, phrase d\'invitation au-dessus du bouton.' },
      { type: '+', text: 'Témoignage d\'Isabelle réordonné avec un portrait aquarelle et la mention de confidentialité.' },
    ],
  },
  {
    version: 'v1.65.1',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : nouveau titre du hero et 200+ personnes',
    items: [
      { type: '~', text: 'H1 : « Accéder à votre sérénité naturelle » au lieu de « Retrouver votre sérénité naturelle ».' },
      { type: '~', text: 'Preuve sociale corrigée : 200+ personnes accompagnées (au lieu de 300+), dans le formulaire et la section Bertrand.' },
    ],
  },
  {
    version: 'v1.65.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : phrase du hero et témoignage pleine largeur',
    items: [
      { type: '~', text: 'Hero : « j\'aide votre corps à sortir du mode « adaptation et survie ». Étape par étape, à votre rythme, il réapprend à se sentir en sécurité. »' },
      { type: '~', text: 'Témoignage de Virginie aligné sur la largeur de la grille des situations sur ordinateur.' },
    ],
  },
  {
    version: 'v1.64.1',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : texte des situations en graisse normale',
    items: [
      { type: '~', text: 'Texte des 6 cartes « Cochez ce qui vous ressemble » en Inter 400 au lieu de 600.' },
    ],
  },
  {
    version: 'v1.64.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : situations réécrites, conclusion dynamique',
    items: [
      { type: '~', text: 'Situations : nouveau surtitre « 10 secondes pour savoir si c\'est pour vous » et 6 nouveaux textes (Inter 600).' },
      { type: '+', text: 'Conclusion dynamique : pastille « n cases cochées » et phrase selon le nombre de cases (0, 1-2, 3-6), annoncée aux lecteurs d\'écran.' },
      { type: '+', text: 'Micro-texte « Appel de 15 min · Sans engagement » sous chaque bouton de section et dans le footer.' },
      { type: '~', text: 'Raccord sans bande entre le hero et les situations ; cartes des situations à l\'arrondi --radius-ui.' },
    ],
  },
  {
    version: 'v1.63.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : témoignage Virginie, section « apnée » retirée',
    items: [
      { type: '~', text: 'Conclusion des situations : « Une seule case cochée ? J\'ai ouvert ce cabinet pour des personnes comme vous, et comme moi. »' },
      { type: '+', text: 'Témoignage long de Virginie P. sous les situations, avec portrait aquarelle.' },
      { type: '-', text: 'Section « Vous n\'avez pas construit tout ça pour vivre en apnée » retirée ; ses idées clés passent dans « Pourquoi ? » et « Qui vous accueille ».' },
      { type: '-', text: 'Mention « Cet appel ne vous oblige en rien » retirée sous tous les boutons.' },
    ],
  },
  {
    version: 'v1.62.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : hero épuré, pop-up de rappel, arrondis',
    items: [
      { type: '-', text: 'Hero : ligne de crédibilité retirée, la carte témoignage suit le bouton.' },
      { type: '~', text: 'Pop-up de rappel : « C\'est moi, Bertrand, qui vous rappelle. », nouveau sous-titre, bouton « Rappelez-moi » en lapis, preuve « 300+ personnes accompagnées · 5,0 sur Google » sous le bouton.' },
      { type: '~', text: 'Arrondi unique --radius-ui (1rem) pour le bouton principal, le bouton menu, la carte témoignage, la pop-up et ses champs ; logo à 8px.' },
      { type: '~', text: '« 300+ séances » devient « 300+ personnes accompagnées ».' },
    ],
  },
  {
    version: 'v1.61.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : « Cochez ce qui vous ressemble »',
    items: [
      { type: '+', text: 'Nouvelle section après le hero : 6 situations à cocher (cases natives, coche dessinée, sans JavaScript), conclusion et bouton.' },
      { type: '-', text: 'Bande de chiffres sous le hero et cartes « fonctionnements » (En surface / Dans le corps) retirées.' },
      { type: '~', text: 'Témoignages : Katherine sous les situations, Nathalie dans « Le cadre ».' },
      { type: '+', text: 'Suivi Matomo des cases cochées et du bouton de la section (aucun envoi à Meta ni Google).' },
    ],
  },
  {
    version: 'v1.60.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : « Retrouver votre sérénité naturelle »',
    items: [
      { type: '~', text: 'Hero : H1 « Retrouver votre sérénité naturelle », paragraphe sous le H1, bouton et micro-texte groupés, crédibilité au-dessus de la carte témoignage.' },
      { type: '~', text: 'Section formulaire : « Commencer par une conversation », appel gratuit de 15 minutes, phrase sous le bouton d\'envoi.' },
      { type: '~', text: 'Titres H1, H2 et H3 sans point final sur toute la page.' },
    ],
  },
  {
    version: 'v1.59.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : hero « Réapprendre à sortir de l\'alerte »',
    items: [
      { type: '~', text: 'Hero refondu : H1 "Réapprendre à sortir de l\'alerte", crédibilité avant le bouton, bouton compact, témoignage aquarelle en verre dépoli ; durée d\'appel harmonisée à 15 min.' },
    ],
  },
  {
    version: 'v1.58.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : hero refondu',
    items: [
      { type: '~', text: 'Hero refondu : surtitre, H1 symptômes, bouton centré, crédibilité sous le bouton, carte témoignage aquarelle ; durée d\'appel harmonisée à 15 min.' },
    ],
  },
  {
    version: 'v1.57.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : hero resserré, carte témoignage réordonnée',
    items: [
      { type: '~', text: 'Hero : surtitre « Approche somatique · Rixensart » en ambre clair, nouveau H1 (soulager les tensions, le manque de sommeil et la surcharge), ligne de crédibilité au-dessus du bouton, micro-texte « Un échange de 10 à 15 minutes, sans engagement. »' },
      { type: '-', text: 'Hero : paragraphe d\'accompagnement et lien « Comment se passe une séance » retirés.' },
      { type: '~', text: 'Carte témoignage : citation, 5 étoiles, signature (pastille « C » en attendant l\'illustration aquarelle).' },
      { type: '~', text: 'Bouton Cookies réduit en pastille sur mobile, sur cette landing.' },
    ],
  },
  {
    version: 'v1.56.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : hero recentré, appel de 10 à 15 min',
    items: [
      { type: '~', text: 'Hero : surtitre « Approche somatique », H1 tensions / sommeil / surcharge, bouton « Demander un appel gratuit », lien « Comment se passe une séance », carte témoignage avec la note Google en pied.' },
      { type: '-', text: 'Hero : badge Privilège automne, ligne de crédibilité et réassurance retirés (l\'offre reste dans le formulaire et les informations pratiques).' },
      { type: '~', text: 'Durée du premier appel harmonisée à 10 à 15 min sur toute la page (boutons, formulaire, modal, meta).' },
    ],
  },
  {
    version: 'v1.55.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : nouveau hero',
    items: [
      { type: '~', text: 'Hero : H1 bénéfice, ligne de crédibilité, bouton "Prendre contact", témoignage anonymisé.' },
    ],
  },
  {
    version: 'v1.54.0',
    date: '13 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : cartes « fonctionnements » et « Pour qui »',
    items: [
      { type: '~', text: 'Landing première séance : cartes "fonctionnements" (En surface / Dans le corps), ajout transition et situation de crise dans "Pour qui".' },
      { type: '~', text: 'Section "Pour qui" réécrite côté vécu, cadre condensé en 3 points.' },
      { type: '+', text: 'Ajout de la carte sommeil en tête, grille 6 cartes.' },
    ],
  },
  {
    version: 'v1.53.0',
    date: '12 octobre 2026',
    badge: 'Contenu',
    badgeColor: 'amber',
    title: 'Landing première séance : découverte en deux rendez-vous',
    items: [
      { type: '~', text: 'Landing première séance : découverte en deux rendez-vous, Privilège automne 60 € au lieu de 70 €, durée 50 min.' },
    ],
  },
  {
    version: 'v1.52.0',
    date: '24 septembre 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Pixel Meta : PageView envoyé après « Tout accepter »',
    items: [
      { type: 'x', text: 'Après un clic sur « Tout accepter », le PageView part désormais une fois sur la page où le consentement est donné, suivi du Lead sur la page de confirmation' },
      { type: '~', text: 'Un seul émetteur du PageView, <MetaPixel /> : une fois par page, que le consentement soit antérieur, donné sur la page ou suivi d’une navigation interne' },
      { type: '~', text: 'Inchangé : refus par défaut (highPrivacy), aucun appel à Meta avant consentement, aucun en cas de refus' },
    ],
  },
  {
    version: 'v1.51.0',
    date: '24 septembre 2026',
    badge: 'SEO',
    badgeColor: 'lapis',
    title: 'Page auteur Bertrand Jacob et signature éditoriale des articles',
    items: [
      { type: '+', text: 'Nouvelle page profil /fr/auteurs/bertrand-jacob : parcours, formations, manière de travailler, cadre, politique éditoriale et liste des articles relus et validés' },
      { type: '+', text: 'Chaque article affiche la signature « Rédigé par l’équipe Amargi · Relu et validé par Bertrand Jacob », avec ses dates de publication et de mise à jour' },
      { type: '~', text: 'L’encadré de fin d’article devient « Relecture et validation », avec photo, parcours et cadre de l’accompagnement' },
      { type: '~', text: 'Données structurées : l’équipe éditoriale Amargi est l’auteur, Bertrand Jacob le relecteur (reviewedBy), sans données auteur dupliquées' },
      { type: '~', text: 'Pied de page et navigation : le lien « L’auteur » remplace « À propos » et pointe vers la nouvelle page' },
      { type: '-', text: 'Ancienne page /fr/auteur redirigée (301), comme /fr/bj et /en/author' },
    ],
  },
  {
    version: 'v1.50.0',
    date: '7 septembre 2026',
    badge: 'Correctif',
    badgeColor: 'lapis',
    title: 'Bandeau cookies — disposition mobile',
    items: [
      { type: 'x', text: 'Sur mobile, « Personnaliser » tombait seul en bas à droite et le lien légal se collait aux deux boutons principaux' },
      { type: '~', text: 'Disposition remise à plat : le texte sur une ligne, les trois boutons côte à côte à hauteur égale, puis le lien « Politique de confidentialité » seul — le tout aligné à gauche sur la marge du site' },
      { type: 'x', text: 'Le bandeau perd 29px de hauteur sur mobile, ce qui dégage complètement le bas du hero sur les petits écrans' },
    ],
  },
  {
    version: 'v1.49.0',
    date: '7 septembre 2026',
    badge: 'Interface',
    badgeColor: 'amber',
    title: 'Retouches du bandeau cookies et du hero',
    items: [
      { type: 'x', text: 'Page Première séance : le hero remonte de 15px sur mobile — sur iPhone, la bannière rognait les deux lignes de la phrase de réassurance' },
      { type: '~', text: 'Le lien « Politique de confidentialité » passe à gauche, aligné sur le texte du bandeau plutôt qu’en bout de la rangée de boutons' },
      { type: '~', text: 'La pastille de réouverture en bas à droite abandonne le cadenas orange vif du plugin pour un discret « Cookies » aux couleurs Amargi' },
    ],
  },
  {
    version: 'v1.48.0',
    date: '7 septembre 2026',
    badge: 'Interface',
    badgeColor: 'amber',
    title: 'Bandeau de consentement aux couleurs Amargi',
    items: [
      { type: '~', text: 'Le bandeau cookies adopte la charte du site : police Inter, fond paper, boutons ambre — le jaune d’origine de tarteaucitron disparaît' },
      { type: '~', text: '« Tout accepter » et « Tout refuser » ont exactement le même poids visuel : refuser reste aussi simple qu’accepter' },
      { type: '~', text: 'Le lien « Politique de confidentialité » passe en lien discret plutôt qu’en quatrième bouton — toujours visible et cliquable' },
      { type: '~', text: 'Bandeau plus aéré et aligné sur la colonne de texte du reste du site' },
      { type: 'x', text: 'Hero de la page d’accueil : le bloc titre était 64px trop bas et la bannière cookies en recouvrait le bas sur les petits écrans. Il est désormais réellement centré, bannière ouverte comme fermée' },
      { type: 'x', text: 'Hero de la page Première séance : le bloc était calé en bas et la bannière recouvrait la vignette « 300+ séances au cabinet » et la phrase de réassurance. Même centrage appliqué' },
    ],
  },
  {
    version: 'v1.47.0',
    date: '7 septembre 2026',
    badge: 'Correctif',
    badgeColor: 'lapis',
    title: 'Adresse de la page de confirmation',
    items: [
      { type: 'x', text: 'La page de confirmation revient sur /fr/merci-contact : la mise à jour précédente l’avait déplacée par erreur' },
      { type: '~', text: 'Les aperçus de partage, l’image dédiée et le noindex de la version précédente sont conservés — ils changent simplement d’adresse' },
      { type: '~', text: 'L’adresse utilisée entre-temps redirige en 301, les liens partagés dans l’intervalle restent valides' },
    ],
  },
  {
    version: 'v1.46.0',
    date: '7 septembre 2026',
    badge: 'SEO',
    badgeColor: 'taupe',
    title: 'Image d’aperçu par défaut',
    items: [
      { type: 'x', text: 'Les dimensions annoncées aux réseaux sociaux (1200×630) ne correspondaient pas à l’image réelle (1536×1024) : l’aperçu était recadré de travers sur 60 des 82 pages du site' },
      { type: '~', text: 'Image d’aperçu par défaut régénérée en 1200×630 — vrai JPEG de 103 ko, là où le fichier précédent était un PNG de 1,3 Mo mal nommé' },
      { type: '~', text: 'Aperçu servi en JPEG et non en WebP : LinkedIn n’affiche pas les images WebP, WhatsApp et iMessage restent inégaux dessus' },
      { type: '+', text: 'Test automatique avant chaque déploiement : les dimensions annoncées sont relues dans le fichier image lui-même, sur toutes les pages' },
    ],
  },
  {
    version: 'v1.45.0',
    date: '7 septembre 2026',
    badge: 'SEO',
    badgeColor: 'taupe',
    title: 'Aperçus de partage — page de confirmation',
    items: [
      { type: 'x', text: 'Partagée sur WhatsApp, iMessage ou LinkedIn, la page de confirmation affichait l’image et la description de la page d’accueil : l’URL n’avait pas de page pré-rendue et le serveur renvoyait le shell du site' },
      { type: '~', text: 'La page de confirmation passe de /fr/merci-contact à /fr/premiere-seance/merci — l’ancienne adresse redirige en 301, les liens déjà partagés restent valides' },
      { type: '+', text: 'Titre, description, Open Graph et Twitter Card propres à la page, écrits dans le HTML statique ; image de partage dédiée en 1200×630' },
      { type: 'x', text: 'Un seul bandeau de consentement sur la page de confirmation : le réglage tarteaucitron pointait encore sur l’ancienne adresse' },
      { type: '+', text: 'Test de non-régression SEO sur cette page, joué avant chaque déploiement' },
    ],
  },
  {
    version: 'v1.44.0',
    date: '7 septembre 2026',
    badge: 'Confidentialité',
    badgeColor: 'ventral',
    title: 'Consentement aux cookies et Pixel Meta',
    items: [
      { type: '+', text: 'tarteaucitron.js 1.34.0 auto-hébergé (public/vendors/tarteaucitron/) : la bannière de consentement ne dépend d’aucun CDN tiers' },
      { type: '+', text: 'Nouveau composant <MetaPixel /> : le Pixel Meta n’est chargé qu’après acceptation explicite — refus par défaut (highPrivacy)' },
      { type: '+', text: 'Pixel câblé sur /fr/premiere-seance (PageView) et /fr/merci-contact (PageView + Lead)' },
      { type: '~', text: 'L’ancien bandeau cookies maison s’efface sur ces deux pages, où tarteaucitron affiche le sien — refuser tout y est aussi direct qu’accepter' },
    ],
  },
  {
    version: 'v1.43.0',
    date: '7 septembre 2026',
    badge: 'Ergonomie',
    badgeColor: 'lapis',
    title: 'Formulaire de contact — confort mobile',
    items: [
      { type: 'x', text: 'Champs passés à 16px : Safari iOS ne zoome plus au focus (la page se recadrait et l’en-tête de la modal était tronqué)' },
      { type: '~', text: 'À l’ouverture de la modal, le focus va au panneau et non au premier champ — le clavier ne masque plus le formulaire' },
      { type: '~', text: 'Modal : en-tête collant (la croix reste atteignable clavier ouvert), hauteur en dvh et marge basse env(safe-area-inset-bottom)' },
      { type: '~', text: 'Modal allégée : titre « Vos coordonnées » sur une ligne (21px mobile) et sous-titre retiré — il faisait doublon avec la section et la note de bas' },
      { type: '~', text: 'Numéro de portable désormais obligatoire (touche entrée = envoi sur mobile)' },
    ],
  },
  {
    version: 'v1.42.0',
    date: '7 septembre 2026',
    badge: 'Fonctionnalité',
    badgeColor: 'lapis',
    title: 'Formulaire de contact — page Première séance',
    items: [
      { type: '+', text: 'Nouveau composant <ContactForm /> : prénom et nom, email, portable (facultatif), envoi direct vers Supabase' },
      { type: '+', text: 'Validation du numéro de portable au format belge (mobile et fixe, +32 ou 0) — champ facultatif, vérifié seulement s\'il est rempli' },
      { type: '+', text: 'Nouvelle modal <ContactModal /> ouverte par tous les boutons « Demander un rendez-vous » de /fr/premiere-seance (Escape, clic sur le fond et focus piégé)' },
      { type: '+', text: 'Nouvelle page de confirmation /fr/merci-contact (noindex), distincte de /fr/merci réservée au paiement' },
      { type: '+', text: 'Table Supabase contact_requests (migration 003) — insertion anonyme, contacts tagués source = formulaire_contact' },
      { type: '-', text: 'Formulaire Kajabi embarqué retiré de la page Première séance (iframe et styles associés)' },
    ],
  },
  {
    version: 'v1.41.0',
    date: '18 août 2026',
    badge: 'Performance',
    badgeColor: 'taupe',
    title: 'Performance & Accessibilité',
    items: [
      { type: '~', text: 'Images hero responsives sur tous les articles (srcset 600/900/1200px) — LCP mobile amélioré' },
      { type: '~', text: 'Code splitting des articles de blog (React.lazy) — bundle JS réduit par article' },
      { type: 'x', text: 'Correction ratio hero images 1200×675 → 1200×630' },
      { type: '+', text: 'Nouveau token --color-taupe-accessible (#6B5A4E) — contraste WCAG AA sur figcaptions et textes secondaires' },
      { type: 'x', text: 'Bouton CTA : text-lapis-deep → text-ink sur fond amber (contraste 6.17:1)' },
      { type: 'x', text: 'Suppression preload parasite /amargi-icon.svg (fetchPriority="low" sur logo nav)' },
      { type: '+', text: 'Redirection 301 trailing slash articles blog' },
      { type: '~', text: 'robots.txt : Disallow: /blog/ précisé' },
      { type: '+', text: 'ART010 ajouté au gate SEO CI' },
    ],
  },
  {
    version: 'v1.40.0',
    date: 'août 2026',
    badge: 'Éditorial',
    badgeColor: 'amber',
    title: 'Refonte éditoriale v2.0 — ART-002 + ART-008 + ART-009',
    items: [
      { type: '~', text: 'ART-002 entièrement réécrit selon le framework éditorial v2.0 (voix "Je" narratrice, vignettes clients composites, bloc disclaimer médical)' },
      { type: '+', text: 'ART-008 publié avec composants KeyPoints et ArticleCTA' },
      { type: '+', text: 'ART-009 publié avec composants KeyPoints et ArticleCTA' },
      { type: '~', text: 'Terminologie "NS Grader" remplacée par "Évaluation Amargi" sur l\'ensemble du site et des articles' },
    ],
  },
  {
    version: 'v1.39.1',
    date: 'août 2026',
    badge: 'SEO / Infra',
    badgeColor: 'lapis',
    title: 'Sitemap manuel post-indexation + nettoyage branches',
    items: [
      { type: '+', text: 'Workflow sitemap documenté — chaque nouvel article déclenche une mise à jour de public/sitemap.xml et une demande d\'indexation manuelle via Google Search Console' },
      { type: '~', text: 'Suppression des branches fusionnées claude/... (nettoyage bulk)' },
      { type: '~', text: 'public/sitemap.xml mis à jour avec ART-008 et ART-009' },
    ],
  },
  {
    version: 'v1.39.0',
    date: 'août 2026',
    badge: 'Modèle commercial',
    badgeColor: 'lapis',
    title: 'Structure commerciale Cabinet Cohérence — Bilan + Protocole + Journée',
    items: [
      { type: '+', text: 'Bilan de Résilience (60 €, 1h) — document PDF pré-séance structuré sur 5 axes (Sécurité, Charge, Récupération, Adaptabilité, Voix), mise en page magazine A4, typographie Fraunces/Inter, palette Amargi' },
      { type: '+', text: 'Protocole Ancrage (900 €, 6 séances / 3 mois) — offre cabinet principale' },
      { type: '+', text: 'Journée Cohérence (~290–350 €, 1 samedi/mois, 8 participants) — format collectif servant de canal d\'acquisition cabinet' },
      { type: '~', text: 'Règle Hormozi appliquée — publicité payante suspendue jusqu\'à 20 ventes manuelles et 10 témoignages quantifiés' },
    ],
  },
  {
    version: 'v1.38.2',
    date: '31 juillet 2026',
    badge: 'Qualité',
    badgeColor: 'taupe',
    title: 'Audit design article — contraste WCAG AA, typographie, conformité DS v2.6',
    items: [
      { type: '~', text: 'Contraste text-ink/45 porté à text-ink/65 sur breadcrumbs, dates, figcaptions, liens TOC et textes secondaires — conformité WCAG AA garantie sur fond Paper' },
      { type: '~', text: 'H2 articles : 40px, margin-top 72px — hiérarchie typographique clarifiée' },
      { type: '~', text: 'H3 articles : 24px, font-weight 400 — distinction niveaux renforcée' },
      { type: '~', text: 'Images corps : border-radius 16px + légende Inter 13px text-taupe' },
      { type: '~', text: 'Bullets Points-clés : tirets taupe discrets (remplacement des puces génériques)' },
      { type: '~', text: 'CTA intermédiaire : style typographique (encart supprimé)' },
      { type: '~', text: 'Conformité auteur : byline article alignée avec JSON-LD (Bertrand Jacob, pas "équipe Amargi")' },
    ],
  },
  {
    version: 'v1.38.1',
    date: '31 juillet 2026',
    badge: 'SEO / Infra',
    badgeColor: 'lapis',
    title: 'Audit JSON-LD complet + fix .htaccess SSG',
    items: [
      { type: '+', text: 'BreadcrumbList JSON-LD ajouté sur les 7 articles (ART-001 à ART-007)' },
      { type: '~', text: 'Champ image ajouté dans le JSON-LD des 6 articles manquants' },
      { type: '~', text: 'author.url corrigé : /a-propos → /fr/auteur sur tous les articles' },
      { type: '~', text: 'og:type corrigé en article pour les pages blog (était figé à website)' },
      { type: '~', text: 'Dates JSON-LD converties au format ISO 8601 complet (YYYY-MM-DDT00:00:00+02:00) sur les 7 articles' },
      { type: 'x', text: 'Bug critique .htaccess : les règles de réécriture ciblaient le format fichier plat (dist/fr/blog.html) incompatible avec le format dossier vite-react-ssg (dist/fr/blog/index.html) — le SSG était silencieusement court-circuité depuis le lancement, Google recevait un shell React vide. Ajout de DirectorySlash Off + condition !-d, suppression de 3 règles mortes.' },
    ],
  },
  {
    version: 'v1.37.1',
    date: '23 juillet 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Corrections blog : image hero ART-004, ordre articles, hero card',
    items: [
      { type: 'x', text: 'Image hero manquante sur ART-004 (nerf-vague-role-anatomie-stress) : prop avifImage rendue optionnelle dans BlogArticleLayout.tsx — la source AVIF n\'est générée que si le fichier existe explicitement' },
      { type: 'x', text: 'Preload regex étendu à .webp (était limité à .png, .jpg, .jpeg)' },
      { type: 'x', text: 'Ordre chronologique du blog : ART-003 (plus récent) promu en carte hero, ART-001 repositionné en troisième carte' },
      { type: 'x', text: 'Format hero card persistant après ajout d\'un nouvel article (bug isFeatured non recalculé dynamiquement)' },
      { type: 'x', text: 'DNS Google Search Console : enregistrement TXT de vérification ajouté dans la zone Infomaniak (était appliqué par erreur dans la zone OVH inactive)' },
    ],
  },
  {
    version: 'v1.36.1',
    date: '8-10 juillet 2026',
    badge: 'UX / Blog',
    badgeColor: 'amber',
    title: 'Sprint UX blog : composants partagés, sidebar, TOC, tables',
    items: [
      { type: '+', text: 'Composants partagés KeyPoints et ArticleCTA créés dans src/components/blog/ et déployés sur ART-001 à ART-006' },
      { type: '~', text: 'TOC mobile : accordéon details natif — accessible sans JS' },
      { type: 'x', text: 'Sidebar sticky : bug IntersectionObserver (boundingClientRect.top < TOP_OFFSET) — logique 3 états (normal → fixed → anchored) évitant le chevauchement avec le footer' },
      { type: 'x', text: 'Z-index sidebar : StickyAside ne passe plus au-dessus du header' },
      { type: '~', text: 'Tables articles : classe .article-table unifiée (header #1B2A3A, alternance de lignes, padding cohérent)' },
      { type: 'x', text: 'Boutons CTA inline : harmonisés en Lapis (#2B5F8E) sur tous les articles (étaient verts/sauge)' },
      { type: '~', text: 'Logo retiré de la sidebar CTA card (redondant avec le header)' },
      { type: 'x', text: 'Category tag ART-002 : "Cluster" → "Neurosciences & Performance"' },
      { type: '+', text: 'Lien "Journal" ajouté dans la navigation principale entre Science et Tarifs' },
      { type: '~', text: 'CTA header : "Commencer maintenant" → "Essayer Amargi"' },
    ],
  },
  {
    version: 'v1.38.0',
    date: '1er juillet 2026',
    badge: 'Performance',
    badgeColor: 'taupe',
    title: 'Optimisation CO2 & LCP — pages blog (PR #48)',
    items: [
      { type: '+', text: 'Image de couverture chargée en priorité — le navigateur ne retarde plus l\'image la plus visible de chaque article, gain direct sur le LCP mobile.', detail: 'BlogArticleLayout.tsx : loading="eager" + fetchPriority="high" sur l\'image de couverture.' },
      { type: '+', text: 'Préchargement de l\'image de couverture dans le <head> — élimine le délai de découverte tardive et renforce le gain LCP.', detail: 'Balise <link rel="preload"> injectée statiquement dans le <head> à chaque article.' },
      { type: '+', text: 'Images de couverture converties en AVIF — 46 Ko contre 586 Ko en PNG d\'origine, poids divisé par environ 12, empreinte CO2 réduite en proportion.' },
      { type: '+', text: 'Préconnexion au domaine analytics — la résolution DNS et le handshake TLS sont effectués en amont, avant que la ressource tierce soit demandée.', detail: '<link rel="preconnect"> et <link rel="dns-prefetch"> ajoutés dans le <head>.' },
      { type: '~', text: 'Polices (Fraunces / Inter) : subset latin explicite et font-display: swap garanti — le texte s\'affiche immédiatement avec une police système au lieu de rester invisible pendant le chargement.' },
      { type: '~', text: 'Cache HTML SSG revalidé à chaque déploiement — garantit que le contenu affiché est toujours à jour après une mise en ligne.' },
      { type: '~', text: 'Audit bundle JS blog : aucun SDK lourd détecté, poids JS transféré sur une page article resté minimal.' },
    ],
  },
  {
    version: 'v1.37.0',
    date: '1er juillet 2026',
    badge: 'Performance',
    badgeColor: 'taupe',
    title: 'Audit perf global — homepage 83→93, quiz 84→90, blog 80→89 (Lighthouse mobile)',
    items: [
      { type: '+', text: 'En-têtes de cache immutable sur tous les assets hashés Vite (JS, CSS, polices, images) — le navigateur ne re-télécharge plus ce qui n\'a pas changé entre deux visites.', detail: 'public/.htaccess : Cache-Control max-age=31536000, immutable sur js/css/woff2/avif/webp.' },
      { type: '~', text: 'Correction d\'un bug de chunking Vite qui forçait le chargement de 176 Ko de PDF (jspdf + html2canvas) sur toutes les routes — ces modules ne sont plus chargés que si nécessaire.', detail: 'vite.config.ts : pin \\0vite/preload-helper.js → react-vendor pour isoler le chunk pdf-export.' },
      { type: '~', text: 'Animations (Framer Motion) : passage en mode LazyMotion + domAnimation sur 13 composants — seules les animations réellement utilisées sont chargées, le reste est écarté du bundle.', detail: 'LandingPage, Quiz, ProfileScreen, ResultCard, ComptePage, DashboardPage, ExperimentationModule, FeedbackScreen, AuthCallbackPage, AuthModal, AuthorPage, PaywallModal.' },
      { type: '~', text: 'Initialisation Supabase différée via requestIdleCallback — le thread principal est libéré au moment du First Contentful Paint, la connexion auth s\'établit quand le navigateur est disponible.' },
      { type: '-', text: 'Suppression complète de purge-firebase.js et de sa balise <script> dans index.html — résidu de la migration Firebase→Supabase qui se chargeait inutilement sur chaque page.' },
      { type: '~', text: 'Fix CLS sur les pages article (CLS 0.022 → 0) : le ratio d\'aspect de l\'image de couverture est désormais déclaré correctement, plus de saut de mise en page au chargement.' },
      { type: '+', text: 'llms.txt publié à la racine du site pour la navigation agentique (format llms.txt standard).' },
    ],
  },
  {
    version: 'v1.36.0',
    date: '1er juillet 2026',
    badge: 'Paiement',
    badgeColor: 'amber',
    title: 'Mollie Live, dashboard 4 états, plan Fondatrice',
    items: [
      { type: '+', text: 'Mollie Live activé — premier vrai paiement encaissé' },
      { type: '+', text: 'Dashboard adapté aux 4 états : free, trial_8j, pro, beta' },
      { type: '+', text: 'Plan Fondatrice 34,50 € ajouté (cohorte Cèdre)' },
      { type: '+', text: 'Champ cohort_name ajouté en base de données' },
      { type: '-', text: 'Suppression du timer de redirection sur BienvenueProPage' },
      { type: '-', text: 'Suppression du plan trial_7j (unifié en trial_8j)' },
    ],
  },
  {
    version: 'v1.35.0',
    date: '29 juin 2026',
    badge: 'Infra',
    badgeColor: 'lapis',
    title: 'Migration Mollie, Edge Functions Deno, sync GitHub Actions → VPS, UX tarifs, fix routes 404, suppression Firebase GCP',
    items: [
      { type: '+', text: 'Migration paiement vers Mollie' },
      { type: '+', text: 'Edge Functions Deno déployées' },
      { type: '+', text: 'Sync GitHub Actions → VPS opérationnel' },
      { type: '~', text: 'UX tarifs améliorée' },
      { type: '~', text: 'Fix routes 404 (dont /fr/changelog → /fr/mises-a-jour, /fr/design-system → /designsystem)' },
      { type: '-', text: 'Suppression résidus Firebase GCP' },
    ],
  },
  {
    version: 'v1.34.1',
    date: '29 juin 2026',
    badge: 'Qualité',
    badgeColor: 'taupe',
    title: 'Nettoyage commentaires Firebase obsolètes',
    items: [
      { type: '~', text: 'Nettoyage commentaires Firebase obsolètes (AuthCallbackPage, NsGraderPro, SiteNav)' },
    ],
  },
  {
    version: 'v1.34.0',
    date: '28 juin 2026',
    badge: 'Infra',
    badgeColor: 'lapis',
    title: 'Migration infrastructure Firebase/Vercel → Supabase self-hosted',
    items: [
      { type: '~', text: 'Migration infrastructure : Firebase/Vercel → Supabase self-hosted (VPS, Docker, Caddy)' },
      { type: '+', text: 'Authentification Google OAuth opérationnelle' },
      { type: '+', text: 'Magic link email opérationnel (flux implicite, callback asynchrone)' },
      { type: '-', text: 'Suppression des résidus Firebase du bundle (−67% taille SDK)' },
      { type: '~', text: 'Fix detectSessionInUrl + page callback sans verdict synchrone' },
      { type: '+', text: 'Redirection post-login vers /fr/tableau-de-bord' },
    ],
  },
  {
    version: 'v1.33.55',
    date: '12 juin 2026',
    badge: 'Légal',
    badgeColor: 'taupe',
    title: 'Conformité RGPD : registre des traitements + acceptation CGU au checkout',
    items: [
      { type: '+', text: 'Page admin /fr/admin/registre-traitements : registre complet des 5 activités de traitement (comptes, sessions, paiements, analytique, hébergement), protégée par email admin.' },
      { type: '+', text: 'Tableau des sous-traitants avec garanties RGPD (CCT, EU-US DPF, PCI-DSS). Fichier registre-traitements.md archivé dans le projet.' },
      { type: '+', text: 'Checkbox CGU obligatoire avant de pouvoir cliquer sur "Essayer Pro" (mensuel et annuel). Liens vers CGU et Politique de confidentialité ouverts dans un nouvel onglet.' },
      { type: '+', text: 'Consentement Stripe : consent_collection terms_of_service required + custom_text dans la session checkout.' },
    ],
  },
  {
    version: 'v1.33.53',
    date: '12 juin 2026',
    badge: 'Légal',
    badgeColor: 'taupe',
    title: 'Restructuration des pages légales',
    items: [
      { type: '+', text: 'Navigation croisée entre les 3 pages légales (Confidentialité, CGU, Mentions légales)' },
      { type: '~', text: 'Contenu RGPD déplacé de Mentions légales vers Confidentialité (page dédiée)' },
      { type: '~', text: 'Mentions légales épurées avec renvoi explicite vers la Politique de confidentialité' },
      { type: '~', text: 'Politique de confidentialité : design harmonisé au système de design Amargi' },
      { type: '~', text: 'Tableau des sous-traitants mis à jour : Vercel, Supabase, Stripe, Kajabi, Google Analytics' },
    ],
  },
  {
    version: 'v1.33.52',
    date: '12 juin 2026',
    badge: 'Feat',
    badgeColor: 'ventral',
    title: 'Nouvelle page CGU (Conditions Générales d’Utilisation)',
    items: [
      { type: '+', text: 'Création de la page /fr/conditions-utilisation avec les 11 articles CGU (description du service, accès, tarifs, droit de rétractation 14 jours, obligations, propriété intellectuelle, limitation de responsabilité, résiliation, données personnelles, modifications, droit applicable).' },
      { type: '+', text: 'Lien CGU ajouté dans le footer (colonne Plus), après Mentions Légales.' },
      { type: '+', text: 'Route /fr/conditions-utilisation enregistrée dans le routeur.' },
    ],
  },
  {
    version: 'v1.33.51',
    date: '11 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Mentions légales : refonte design + contenu RGPD complet',
    items: [
      { type: '~', text: 'Design : typographie corrigée (Fraunces font-light pour H1/H2, Inter text-base 16px pour les paragraphes), séparateurs border-ink/15, blocs info bg-sand/20, bloc contact en carte bordée à la place du fond bg-ink.' },
      { type: '+', text: 'RGPD : ajout Article 03 complet (responsable de traitement, données collectées, bases légales, durée de conservation, droits des utilisateurs avec procédure et délai 30 jours, lien APD belge).' },
      { type: '+', text: 'RGPD : ajout Article 04 sur les cookies et traceurs (techniques, analytiques GA4, paiement Stripe).' },
      { type: '+', text: 'RGPD : ajout Article 05 avec tableau des sous-traitants principaux (Vercel, Supabase, Stripe, OpenAI, Google Analytics) et mention des CCT/EU-US DPF.' },
      { type: '~', text: 'Ponctuation : suppression de tous les tirets cadratins, remplacement par ponctuation française (deux-points, virgules, parenthèses).' },
      { type: '~', text: 'Numérotation : articles renumérotés 01 à 09 pour intégrer les nouveaux articles RGPD.' },
      { type: '~', text: 'Lien retour : ajout hover:text-amber pour cohérence avec les interactions du site.' },
    ],
  },
  {
    version: 'v1.33.50',
    date: '11 juin 2026',
    badge: 'Feat',
    badgeColor: 'ventral',
    title: 'Stripe Tax — calcul automatique de la TVA (OSS UE + Belgique)',
    items: [
      { type: '+', text: 'stripe-checkout.ts : automatic_tax: { enabled: true } — Stripe calcule et applique automatiquement le bon taux de TVA selon le pays du client (OSS UE + immatriculation Belgique).' },
      { type: '+', text: 'stripe-checkout.ts : billing_address_collection: "required" — Stripe collecte l\'adresse complète du client pendant le paiement, indispensable pour déterminer le taux applicable par pays.' },
      { type: '+', text: 'stripe-checkout.ts : customer_creation: "always" — création systématique d\'un Customer Stripe pour mémoriser l\'adresse et assurer le bon calcul de TVA sur les renouvellements d\'abonnement.' },
    ],
  },
  {
    version: 'v1.33.49',
    date: '11 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Mentions légales, meta OG et domaine — correction amargi.me + Vercel',
    items: [
      { type: '~', text: 'MentionsLegales.tsx : remplacement de Kajabi par Vercel Inc. (440 N Baxter Ave, San Francisco, CA 94107) comme hébergeur (Article 02).' },
      { type: '~', text: 'MentionsLegales.tsx : correction de toutes les occurrences "amargis.me" → "amargi.me" (Articles 01, 02, 03, 04, 06).' },
      { type: '~', text: 'index.html : og:url et twitter:url corrigés de nervoussystemgrader.com → https://www.amargi.me/.' },
      { type: '~', text: 'index.html : og:image et twitter:image corrigés de nervoussystemgrader.com → https://www.amargi.me/og-image.jpg.' },
    ],
  },
  {
    version: 'v1.33.48',
    date: '11 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Menu utilisateur desktop — correction fermeture prématurée (group-hover → état React)',
    items: [
      { type: '~', text: 'SiteNav.tsx : le menu déroulant utilisateur (Compte / Se déconnecter) utilisait group-hover CSS pur — il se fermait avant que la souris atteigne les options. Remplacé par un état React userMenuOpen + fermeture au clic extérieur via useRef/mousedown, identique au menu "Plus".' },
      { type: '+', text: 'SiteNav.tsx : ajout d\'un en-tête non-cliquable dans le menu utilisateur affichant le nom ou l\'email de l\'utilisateur connecté pour confirmation visuelle.' },
    ],
  },
  {
    version: 'v1.33.46',
    date: '11 juin 2026',
    badge: 'Feat',
    badgeColor: 'ventral',
    title: 'Migration sessions anonymes → compte lors de la première connexion',
    items: [
      { type: '+', text: 'userSession.ts : nouvelle fonction migrateAnonymousSessions(oldUid, newUid) — copie toutes les sessions (batch Supabase), fusionne les agrégats (totalSessions + longestStreak), et transfère email/prenom/consentGiven de l\'ancien profil anonyme vers le nouveau compte. Idémpotent via le flag localStorage amargi_migration_done.' },
      { type: '+', text: 'useAuth.ts : appel automatique de migrateAnonymousSessions dans onAuthStateChanged — dès qu\'un utilisateur se connecte (Google, Apple, Magic Link) avec un ancien UUID localStorage, ses bilans sont migrés silencieusement en arrière-plan sans bloquer la connexion.' },
    ],
  },
  {
    version: 'v1.33.45',
    date: '11 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Audit flux complet : paiement avant/après login, trial_7j à la connexion, spinner BienvenueProPage',
    items: [
      { type: '~', text: 'useAuth.ts : ensureUserDocOnAuth — le plan initial d\'un nouveau compte passe de \'free\' à \'trial_7j\' avec trialStartedAt. Tout utilisateur qui se connecte pour la première fois bénéficie automatiquement de l\'essai 7 jours.' },
      { type: '~', text: 'BienvenueProPage.tsx : ajout d\'un état waitingForPlan (max 15s) — le compte à rebours ne démarre qu\'une fois le plan mis à jour par le webhook Stripe. Un spinner \'Activation en cours…\' s\'affiche pendant l\'attente.' },
    ],
  },
  {
    version: 'v1.33.44',
    date: '11 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Plan Pro temps réel + sécurisation flux paiement (connexion obligatoire + client_reference_id)',
    items: [
      { type: '~', text: 'useAuth.ts : remplacement de getDoc par onSnapshot — le plan est maintenant écouté en temps réel. Dès que le webhook Stripe met à jour users/{uid}.plan, l\'app se met à jour instantanément sans rechargement.' },
      { type: '~', text: 'PricingPage.tsx : handleCheckout() vérifie maintenant isAuthenticated avant de lancer le paiement. Si l\'utilisateur n\'est pas connecté, l\'AuthModal s\'ouvre automatiquement.' },
      { type: '~', text: 'api/stripe-webhook.ts : checkout.session.completed utilise client_reference_id (uid direct) en priorité, avec fallback sur l\'email.' },
    ],
  },
  {
    version: 'v1.33.43',
    date: '11 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'Boutons de paiement Stripe — migration vers API serveur (stripe-js v9)',
    items: [
      { type: '+', text: 'api/stripe-checkout.ts : nouvelle Vercel Function qui crée la session Checkout côté serveur et redirige vers Stripe (remplace stripe.redirectToCheckout() supprimé dans @stripe/stripe-js v9).' },
      { type: '~', text: 'stripeService.ts : redirectToCheckout() réécrit — redirige vers /api/stripe-checkout?plan=...&uid=... au lieu d\'appeler l\'API Stripe côté client.' },
      { type: '~', text: 'PricingPage.tsx : handleCheckout() mis à jour pour passer l\'uid à redirectToCheckout(), import useAuth ajouté.' },
    ],
  },
  {
    version: 'v1.33.42',
    date: '11 juin 2026',
    badge: 'Nouveau',
    badgeColor: 'lapis',
    title: 'Tunnel Stripe complet — webhook base de données, page bienvenue-pro, portail compte',
    items: [
      { type: '+', text: 'api/stripe-webhook.ts : Vercel Function qui écoute checkout.session.completed, customer.subscription.deleted, invoice.payment_failed et met à jour users/{uid}.plan dans la base de données.' },
      { type: '+', text: 'api/stripe-portal.ts : Vercel Function qui crée une session Stripe Billing Portal et redirige l\'utilisateur vers la gestion de son abonnement.' },
      { type: '+', text: 'BienvenueProPage.tsx : page /fr/bienvenue-pro — confirmation post-paiement, 3 étapes onboarding, compte à rebours 10s vers /fr/evaluation.' },
      { type: '+', text: 'ComptePage.tsx : bouton "Gérer mon abonnement" visible pour les plans pro/trial_7j/trial_8j, appelle /api/stripe-portal.' },
      { type: '~', text: 'stripeService.ts : Price IDs réels configurés (monthly: price_1Th4AF2M9AvDfwxLyUpzugae, annual: price_1Th4At2M9AvDfwxL3EecPMcd), mode subscription, successUrl vers /fr/bienvenue-pro.' },
      { type: '-', text: 'Routes /fr/pro et /fr/bj supprimées (Navigate vers /fr/tarifs et /fr/auteur). Pages NsGraderPro.tsx et BjPage.tsx obsolètes.' },
      { type: '~', text: 'SiteNav.tsx : badge Pro desktop supprimé, lien Pro mobile supprimé, lien Méthode supprimé du menu Plus, displayName Google affiché dans le menu compte.' },
      { type: '~', text: 'useAuth.ts : displayName ajouté dans AuthState pour afficher le prénom Google dans la navigation.' },
    ],
  },
  {
    version: 'v1.33.41',
    date: '10 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'SSG + Vercel — routes /fr/experimentation/:code pré-rendues, 404 évité',
    items: [
      { type: '+', text: 'vite.config.ts : ssgOptions.includedRoutes génère les 27 fichiers HTML statiques (/fr/experimentation/L-L-L … H-H-H) au build.' },
      { type: '+', text: 'vercel.json : routes explicites pour /fr/experimentation/:code, /fr/resultat/:code et /fr/partage/:code — servis avant le filesystem check (contourne le 404 de cleanUrls).' },
      { type: '+', text: 'Cache immutable ajouté sur /assets/* dans vercel.json.' },
      { type: '~', text: 'Avant ce correctif, accéder directement à /fr/experimentation/M-H-M?score=32 affichait une page blanche 404 Vercel.' },
    ],
  },
  {
    version: 'v1.33.40',
    date: '10 juin 2026',
    badge: 'Fix',
    badgeColor: 'crise',
    title: 'PaywallModal — bouton Retour, scrollable sur mobile, navigation débloquée',
    items: [
      { type: '+', text: 'Bouton « ← Retour à l’accueil » ajouté en haut de la modale (utilise la prop onBack déjà passée par Quiz.tsx).' },
      { type: '~', text: 'Corps de la modale rendu scrollable (overflow-y-auto) avec max-h-[90dvh] : plus de contenu coupé sur petits écrans.' },
      { type: '~', text: 'Note de bas de page fixée en bas (flex-shrink-0) avec séparateur discret.' },
    ],
  },
  {
    version: 'v1.33.39',
    date: '10 juin 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Écran exercice — phrase de transition ancrée, largeurs harmonisées, SOS repositionné',
    items: [
      { type: '~', text: 'Phrase de transition profil → exercice déplacée dans la carte timer (séparateur discret en bas), plus d\'effet flottant sans ancrage.' },
      { type: '~', text: 'Largeurs harmonisées : accordéon, carte signaux et bloc preuve sociale alignés sur la même largeur que la carte timer (suppression des mx-4 incohérents).' },
      { type: '~', text: 'Bouton SOS repositionné en bas à droite (bottom-6 right-4), plus discret (fond crise/85, taille réduite).' },
    ],
  },
  {
    version: 'v1.33.38',
    date: '10 juin 2026',
    badge: 'Nouveau',
    badgeColor: 'lapis',
    title: 'Authentification — bouton Connexion/Compte dans la nav, AuthModal sur PricingPage',
    items: [
      { type: '+', text: 'SiteNav : bouton Connexion (non connecté) / Compte (connecté) en desktop et mobile, avec menu déroulant déconnexion.' },
      { type: '+', text: 'SiteNav : AuthModal intégré directement dans la navigation principale.' },
      { type: '+', text: "PricingPage : bouton Reverse Trial \"Commencer l'essai\" ouvre désormais l'AuthModal au lieu de rediriger vers /fr/evaluation." },
      { type: '+', text: 'PaywallModal : AuthModal déjà intégré (v1.33.37) — confirmé fonctionnel.' },
      { type: '~', text: 'Apple Sign-In masqué dans AuthModal (Apple Developer Account requis).' },
    ],
  },
  {
    version: 'v1.33.37',
    date: '10 juin 2026',
    badge: 'Nouveau',
    badgeColor: 'lapis',
    title: 'Authentification complète — Magic Link, Google, Apple',
    items: [
      { type: '+', text: 'useAuth hook : gestion état connexion, migration userId anonyme vers uid authentifié.' },
      { type: '+', text: 'AuthModal : 3 méthodes (Magic Link email, Google Sign-In, Apple Sign-In), design Amargi.' },
      { type: '+', text: 'AuthCallbackPage : page /fr/auth/callback pour compléter la connexion Magic Link.' },
      { type: '+', text: 'AuthProvider : contexte React partagé dans toute l\'application.' },
      { type: '+', text: 'useFreemium : bypass automatique si plan trial_7j / trial_8j / beta / pro.' },
      { type: '+', text: 'ComptePage : page /fr/compte avec email, plan actif, historique 5 sessions, déconnexion.' },
      { type: '+', text: 'App.tsx : routes /fr/auth/callback et /fr/compte ajoutées.' },
    ],
  },
  {
    version: 'v1.33.34',
    date: '10 juin 2026',
    badge: 'Nouveau',
    badgeColor: 'lapis',
    title: 'Page profil Bertrand Jacob (/fr/bj)',
    items: [
      { type: '+', text: 'Nouvelle page /fr/bj : profil complet de Bertrand Jacob, fondateur d\'Amargi.' },
      { type: '+', text: 'Sections : Hero chiffres cles (400+ personnes, 8 pays, 15+ ans), 3 expertises, accompagnements, parcours chronologique, temoignages clients.' },
      { type: '+', text: 'Lien vers bertrandjacob.com et CTA bilan gratuit Amargi.' },
    ],
  },
  {
    version: 'v1.33.33',
    date: '10 juin 2026',
    badge: 'Nouveau',
    badgeColor: 'lapis',
    title: 'Freemium 2/jour, SOS belge, preuve sociale, vagues beta, Reverse Trial',
    items: [
      { type: '+', text: 'Freemium : limite a 2 evaluations par jour (matin + soir), reset minuit, PaywallModal avec compteur temps reel.' },
      { type: '+', text: 'Bouton SOS flottant sur ecran exercice : protocole crise 5 etapes + 4 numeros belges cliquables (0800 32 123, 0800 58 181, 112, 02 512 30 30).' },
      { type: '+', text: 'Preuve sociale in-app : bloc stats anonymisees sous la carte signaux.' },
      { type: '+', text: 'BetaPage : systeme 3 vagues (juin 10 places, juillet 30, aout 100) avec badge Active anime.' },
      { type: '+', text: 'PricingPage : bloc Reverse Trial 7 jours + carte Gratuit mise a jour (2 bilans/jour).' },
    ],
  },
  {
    version: 'v1.33.32',
    date: '10 juin 2026',
    badge: 'Nouveau',
    badgeColor: 'lapis',
    title: 'Page Beta : programme beta testeurs Amargi',
    items: [
      { type: '+', text: 'Nouvelle page /fr/beta : pitch du programme, waiting list, blocs 8 jours et pourquoi votre retour compte.' },
      { type: '+', text: 'Formulaire inscription beta avec question de qualification, stockage en base de données (collection beta_waitlist).' },
      { type: '+', text: 'Bloc changelog editorial : les 5 dernieres evolutions presentees de maniere lisible.' },
      { type: '+', text: 'FAQ 5 questions, CTA final vers le bilan.' },
    ],
  },
  {
    version: 'v1.33.31',
    date: '10 juin 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Phrase de transition profil vers exercice',
    items: [
      { type: '+', text: 'Phrase contextuelle avant chaque exercice : Parce que votre systeme est en etat de [X], nous commençons par [nom exercice].' },
      { type: '+', text: 'Mapping des 27 profils sur 9 labels lisibles (fermeture dorsal-vagale, hypervigilance, surcharge globale, etc.).' },
    ],
  },
  {
    version: 'v1.33.30',
    date: '10 juin 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'ExerciseStep : style clair fond paper/white',
    items: [
      { type: '~', text: 'Suppression du bloc ink plein largeur : fond paper neutre, coherent avec le reste du site.' },
      { type: '~', text: 'Carte timer sur fond blanc avec bordure sand et ombre legere.' },
      { type: '~', text: 'Timer amber agrandi (clamp 48px-64px), phase et cue en text-ink/text-taupe.' },
      { type: '~', text: 'Barre de progression sur fond limestone, instructions sur fond blanc.' },
      { type: '~', text: 'Bouton Demarrer : bordure sand sur fond paper. Bouton Complete : bg-lapis.' },
    ],
  },
  {
    version: 'v1.33.29',
    date: '10 juin 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Exercice : label sur 1 ligne + texte accordeon agrandi',
    items: [
      { type: '~', text: 'Label exercice (Coach IA · ...) : truncate pour tenir sur 1 ligne sans debordement.' },
      { type: '~', text: "Texte de l'accordeon Pourquoi ca marche : taille augmentee de 13px a 15px." },
    ],
  },
  {
    version: 'v1.33.28',
    date: '10 juin 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Exercice : restructuration — titre dans le bloc ink, accordéon en dessous',
    items: [
      { type: '~', text: 'Bloc timer : retour à ink (lapis trop saturé sur mobile).' },
      { type: '~', text: 'Titre et label exercice intégrés dans le bloc ink (plus de carte blanche séparée).' },
      { type: '~', text: 'Accordéon "Pourquoi ça marche" déplacé en dessous du bloc timer.' },
    ],
  },
  {
    version: 'v1.33.27',
    date: '10 juin 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Exercice : lapis, accordéon, timer réduit + FeedbackScreen scroll + Autre',
    items: [
      { type: '+', text: 'Accordéon "Pourquoi ça marche" : fermé par défaut, dépliable.' },
      { type: '~', text: 'Bloc timer : fond lapis (remplace ink).' },
      { type: '~', text: 'Timer : taille réduite (clamp 44px→56px) pour tenir sur mobile.' },
      { type: '~', text: 'Titre exercice ancré dans la carte (plus de titre de page séparé).' },
      { type: '~', text: 'Boutons : pb-safe pour iOS (env(safe-area-inset-bottom)).' },
      { type: '+', text: 'FeedbackScreen : scroll vers le haut au montage.' },
      { type: '+', text: 'FeedbackScreen : pill "Autre" ajoutée dans les signaux.' },
      { type: '~', text: 'Mobile Quiz : padding supérieur réduit à pt-14.' },
    ],
  },
  {
    version: 'v1.33.26',
    date: '10 juin 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Consignes simplifiées + affichage mobile optimisé',
    items: [
      { type: '~', text: 'Q1 : consigne raccourcie (“Depuis votre chaise…” supprimé).' },
      { type: '~', text: 'Q2 : consigne simplifiée en une seule phrase claire.' },
      { type: '~', text: 'Q3 : consigne réduite à l’essentiel.' },
      { type: '~', text: 'Q4 : “Ramenez-vous” remplacé par “Souvenez-vous”.' },
      { type: '~', text: 'Q5 : “Posez” remplacé par “Déposez”, respiration précisée.' },
      { type: '~', text: 'Q6 : formulation directe “Imaginez que vous devez…”.' },
      { type: '~', text: 'Mobile : padding supérieur réduit (pt-24 → pt-16) pour remonter la carte de question sur écran téléphone.' },
    ],
  },
  {
    version: 'v1.33.25',
    date: '9 juin 2026',
    badge: 'Performance',
    badgeColor: 'sand',
    title: 'Audit et nettoyage du code — −69 % sur ExperimentationModule',
    items: [
      { type: '-', text: 'ExperimentationModule.tsx : suppression de 898 lignes de code mort (MirrorCard, ChallengesStep, ConditionsStep, SignalsStep, NextStepsStep, EXERCISES, SIGNALS_LIST, getExerciseKey, ExerciseKey) — le fichier passe de 1 287 à 389 lignes (−69,8 %).' },
      { type: '-', text: 'Suppression de 3 fichiers obsolètes : profiles_en.ts (1 212 lignes), LanguageDetectionBanner.tsx (130 lignes), LanguageSelector.tsx (46 lignes).' },
      { type: '-', text: 'Suppression des imports inutilisés dans ExperimentationModule : ArrowLeft (Lucide), AppLink, ProfileScreen.' },
      { type: '~', text: 'LandingPage passé en lazy loading pour réduire le bundle initial.' },
      { type: '~', text: 'App.tsx : remplacement des couleurs hardcodées (#F5F0E8, #C06C4D, #2B5F8E) par les tokens Tailwind (paper, amber, lapis, ink, taupe).' },
    ],
  },
  {
    version: 'v1.33.24',
    date: '9 juin 2026',
    badge: 'Parcours',
    badgeColor: 'lapis',
    title: 'Feedback post-exercice et corrections UX',
    items: [
      { type: '+', text: 'Nouvel écran de retour d\'expérience (FeedbackScreen) entre l\'exercice et le Dashboard : 3 questions (signaux observés, ressenti corporel, évaluation de l\'exercice).' },
      { type: '~', text: 'L\'AnalysisScreen n\'affiche plus que la phrase italic et les 3 points animés — le label \'Analyse en cours\' est supprimé.' },
      { type: '~', text: 'Vouvoiement complet dans le Dashboard : \'Votre évolution\' et \'Voici la vôtre.\' remplacent les formes tutoiement.' },
      { type: '~', text: 'Le bouton \'Complété\' dans l\'ExerciseStep mène directement au FeedbackScreen sans étape intermédiaire.' },
      { type: '~', text: 'Les signaux observés et le ressenti corporel sont désormais enregistrés dans la session.' },
    ],
  },
  {
    version: 'v1.33.23',
    date: '9 juin 2026',
    badge: 'Parcours',
    badgeColor: 'lapis',
    title: 'Continuité visuelle, 90 exercices et vouvoiement',
    items: [
      { type: '~', text: 'Le header (SiteNav + ProgressBar) est masqué sur les écrans miroir et profil pour préserver la continuité visuelle du parcours.' },
      { type: '~', text: 'Le TopBar de l\'ExperimentationModule est supprimé : l\'exercice s\'affiche dans le même environnement visuel que le quiz.' },
      { type: '+', text: 'Bibliothèque de 90 exercices (exercises90.ts) : 9 états polyvagaux × 10 exercices, chacun en 3 minutes.' },
      { type: '+', text: 'Sélection aléatoire d\'un exercice parmi les 10 de l\'état correspondant au profil CN-CP-CA.' },
      { type: '~', text: 'La mise en avant des instructions (highlight) est désormais synchronisée sur les phases réelles de l\'exercice.' },
      { type: '~', text: 'Les boutons \'Valider l\'exercice\' et \'Terminer\' redirigent directement vers le Dashboard.' },
      { type: '~', text: 'Vouvoiement complet dans DashboardPage (StreakBadge, ScoreChart, EmptyState, SEO description).' },
    ],
  },
  {
    version: 'v1.33.22',
    date: '9 juin 2026',
    badge: 'Parcours',
    badgeColor: 'lapis',
    title: 'Restructuration du parcours post-bilan',
    items: [
      { type: '~', text: 'Le parcours passe de 6 étapes à 2 : résumé du profil individuel puis exercice personnalisé.' },
      { type: '-', text: 'Suppression des étapes intermédiaires : défis, conditions, signaux observés et suite.' },
      { type: '+', text: 'Après l\'exercice, la session est enregistrée et l\'utilisateur est redirigé vers le Dashboard.' },
      { type: '~', text: 'La route /fr/experimentation/:code affiche désormais directement le profil résumé (ProfileScreen) puis l\'ExerciseStep.' },
    ],
  },
  {
    version: 'v1.33.21',
    date: '9 juin 2026',
    badge: 'Exercice',
    badgeColor: 'lapis',
    title: 'Refonte de l\'interface de l\'exercice guidé',
    items: [
      { type: '~', text: 'Le timer est désormais toujours visible dès l\'arrivée sur l\'écran, sans attendre le clic sur Démarrer.' },
      { type: '~', text: 'Les instructions restent visibles pendant l\'exercice et se surlignent progressivement selon l\'avancement du timer.' },
      { type: '~', text: 'Les signaux de régulation sont présentés dans une carte séparée avec pills propres et note en Fraunces italic.' },
      { type: '~', text: 'L\'interface passe de 3 boutons (Pause / Arrêter / Instructions) à 2 boutons clairs : Démarrer/Pause et Complété.' },
      { type: '+', text: 'Un CTA principal Évaluer cet exercice apparaît en bas de page, distinct des boutons du timer.' },
    ],
  },
  {
    version: 'v1.33.20',
    date: '9 juin 2026',
    badge: 'Évaluation',
    badgeColor: 'lapis',
    title: 'Écran de profil individuel après le bilan',
    items: [
      { type: '+', text: 'Après confirmation de la phrase miroir ("Oui, c\'est ça"), un écran de profil individuel s\'affiche directement dans le parcours — sans redirection.' },
      { type: '+', text: 'L\'écran affiche le nom du profil, son badge d\'état polyvagal, une introduction, ses particularités et les sensations corporelles associées.' },
      { type: '+', text: 'Un bouton CTA lapis invite à obtenir l\'exercice de régulation personnalisé.' },
      { type: '+', text: 'Les 27 profils sont correctement mappés depuis le code CN-CP-CA calculé par le scoring du bilan.' },
      { type: '~', text: 'Le flux "Pas tout à fait" conserve la redirection vers le module d\'expérimentation avec texte libre.' },
      { type: '~', text: 'Écran miroir redesigné : phrase en grand serif sur fond paper, bouton amber, séparateur sable animé, label "VOTRE PROFIL".' },
    ],
  },
  {
    version: 'v1.33.14',
    date: '1 juin 2026',
    badge: 'Évaluation',
    badgeColor: 'lapis',
    title: 'Améliorations UX du module d\'évaluation',
    items: [
      { type: '~', text: 'L\'en-tête de chaque question affiche désormais uniquement « Question X/7 » — sans mention du bloc ou de la dimension technique.' },
      { type: '~', text: 'La consigne somatique (geste à effectuer) est mise en évidence dans un encadré amber, distincte de la question.' },
      { type: '+', text: 'Les questions 4 à 7 disposent désormais chacune d\'une consigne d\'ancrage sensoriel avant la question.' },
      { type: '~', text: 'L\'option « Pas vraiment » sur la phrase miroir ouvre un formulaire de texte libre — la personne peut exprimer ce qu\'elle se dit vraiment avant d\'obtenir son exercice personnalisé.' },
      { type: '-', text: 'Le bloc de transition « Bloc B » entre les questions 3 et 4 a été supprimé.' },
    ],
  },
  {
    version: 'v1.33.13',
    date: '1 juin 2026',
    badge: 'Évaluation',
    badgeColor: 'lapis',
    title: 'Protocole 3+3 — Nouveau module d\'évaluation',
    items: [
      { type: '+', text: 'Le bilan passe de 6 questions génériques à 7 questions somatiques structurées en deux blocs (Bloc A : corps, Bloc B : journée).' },
      { type: '+', text: 'Chaque réponse peut déclencher une sous-question contextuelle pour affiner le profil (ex. charge chronique vs. passagère, énergie qui revient ou non).' },
      { type: '+', text: 'Le scoring calcule désormais trois dimensions indépendantes : Charge Passive (CP), Charge Active (CA) et Charge Neurale (CN).' },
      { type: '+', text: 'Un écran d\'analyse animé apparaît après la dernière question, suivi d\'une phrase miroir personnalisée parmi 27 profils possibles.' },
      { type: '+', text: 'La phrase miroir peut être confirmée ou infirmée par l\'utilisateur avant de passer au résultat.' },
      { type: '~', text: 'Les confirmations de réponse sont désormais contextuelles et reflètent le choix effectué (ex. « Raideur notée » ou « Mouvement fluide noté »).' },
    ],
  },
  {
    version: 'v1.33.12',
    date: '22 mai 2026',
    badge: 'Correction',
    badgeColor: 'lapis',
    title: 'Correction de la mise en page du Design System',
    items: [
      { type: '~', text: 'La section 18 (Responsive & Mobile) est désormais correctement positionnée après la section Moodboard, et non imbriquée à l\'intérieur.' },
      { type: '~', text: 'La section Moodboard utilise maintenant une structure HTML valide — le bloc de contenu principal n\'est plus une balise invalide.' },
      { type: '~', text: 'Le doublon de la section Responsive (qui apparaissait deux fois dans la page) a été supprimé.' },
      { type: '~', text: 'Les numéros de sous-sections Responsive ont été mis à jour (18.1 à 18.7) pour correspondre à la numérotation globale.' },
    ],
  },
  {
    version: 'v1.33.11',
    date: '22 mai 2026',
    badge: 'Qualité',
    badgeColor: 'taupe',
    title: 'Audit rédactionnel du Changelog',
    items: [
      { type: '~', text: 'Les entrées des 10 derniers jours ont été réécrites dans un langage orienté utilisateur et bénéfice.' },
      { type: '~', text: 'Les détails techniques (noms de classes, valeurs CSS) ont été déplacés dans des blocs repliables.' },
      { type: '~', text: 'Les titres en franglais ou partiellement techniques ont été corrigés en français courant.' },
    ],
  },
  {
    version: 'v1.33.10',
    date: '22 mai 2026',
    badge: 'Design System',
    badgeColor: 'amber',
    title: 'Mise à jour du Design System (v2.5)',
    items: [
      { type: '~', text: 'La documentation visuelle d\'Amargi a été mise à jour vers sa dernière version (v2.5).' },
      { type: '~', text: 'L\'icône de l\'onglet (favicon) est désormais correctement affichée sur cette page.', detail: 'Balises favicon (SVG + PNG 32px + PNG 48px + Apple Touch Icon) conservées dans le fichier HTML statique.' },
    ],
  },
  {
    version: 'v1.33.9',
    date: '22 mai 2026',
    badge: 'Fix',
    badgeColor: 'amber',
    title: 'Icône d\'onglet sur la page Design System',
    items: [
      { type: '~', text: 'L\'icône d\'Amargi s\'affiche désormais correctement dans l\'onglet du navigateur pour la page du Design System.', detail: 'Balises favicon ajoutées directement dans le <head> du fichier HTML statique /designsystem/index.html.' },
    ],
  },
  {
    version: 'v1.33.8',
    date: '22 mai 2026',
    badge: 'Fix',
    badgeColor: 'amber',
    title: 'Accessibilité de la page Design System',
    items: [
      { type: '~', text: 'La page du Design System est désormais accessible sans erreur.', detail: 'vercel.json : règle de réécriture SPA mise à jour pour exclure /designsystem.' },
      { type: '~', text: 'L\'icône d\'onglet (favicon) a été vérifiée et validée sur l\'ensemble du site.', detail: 'amargi-favicon.svg (SVG valide) référencé dans index.html global.' },
    ],
  },
  {
    version: 'v1.33.7',
    date: '22 mai 2026',
    badge: 'Design System',
    badgeColor: 'amber',
    title: 'Publication du Design System en ligne',
    items: [
      { type: '+', text: 'Le Design System d\'Amargi est désormais accessible publiquement via une URL dédiée.', detail: 'Page /designsystem servie en statique depuis /public (4 943 lignes HTML).' },
      { type: '+', text: 'Cette documentation permet aux collaborateurs et aux IA d\'accéder aux règles visuelles du projet.', detail: 'Accessible à https://www.amargi.me/designsystem/' },
    ],
  },
  {
    version: 'v1.33.6',
    date: '22 mai 2026',
    badge: 'Design System',
    badgeColor: 'lapis',
    title: 'Amélioration de la lisibilité et du design',
    items: [
      { type: '~', text: 'Typographie affinée : les titres sont plus légers et élégants, améliorant le confort de lecture.', detail: 'H1/H2 passent de font-bold (700) à font-light (300), H3 de font-bold (700) à font-normal (400).' },
      { type: '~', text: 'Petits textes plus lisibles : les surtitres et labels ont été standardisés pour une meilleure clarté.', detail: 'Eyebrows et labels passent à la classe utilitaire label-caps (11px, 500, +0.15em).' },
      { type: '~', text: 'Boutons optimisés : forme légèrement adoucie et pleine largeur sur mobile pour faciliter le clic.', detail: 'Boutons CTA : rounded-xl (16px) → rounded-lg (12px). Ajout w-full sm:w-auto sur mobile.' },
      { type: '~', text: 'Couleurs harmonisées : les fonds de cartes et les états du système nerveux utilisent désormais la palette officielle Amargi.', detail: 'Fonds bg-sand/10 → bg-limestone/40 ou 60. Couleurs hardcodées remplacées par les tokens sémantiques (crise, taupe, amber, ventral, lapis).' },
    ],
  },

  {
    version: 'v1.33.5',
    date: '22 mai 2026',
    badge: 'Design',
    badgeColor: 'lapis',
    title: 'Refonte de la page "Comment ça marche"',
    items: [
      { type: '~', text: 'Mise en page plus aérée : les textes explicatifs sont plus larges et plus faciles à lire.', detail: 'S2 Mécanique biologique : largeur max-w-[760px] → max-w-[1100px] pour grille 2 colonnes.' },
      { type: '~', text: 'Typographie du corps de texte mise à jour pour correspondre à notre identité visuelle.', detail: 'Corps de texte : font-serif text-[19px] → font-sans text-[16px] (Fraunces réservé aux titres).' },
      { type: '~', text: 'Parcours simplifié : la chronologie en zigzag a été remplacée par une grille claire et numérotée.', detail: 'S4 Timeline zigzag supprimée : remplacée par grille 2 colonnes de cartes numérotées.' },
      { type: '~', text: 'Respiration visuelle : l\'espacement entre les sections a été augmenté pour un défilement plus agréable.', detail: 'Espacement global : py-24 → py-28 sur toutes les sections principales.' },
      { type: '~', text: 'Correction d\'un problème d\'affichage mineur sur le graphique des états.', detail: 'Correction erreur CSS : double attribut className sur div gradient axe des états.' },
    ],
  },

  {
    version: 'v1.33.1',
    date: '22 mai 2026',
    badge: 'Layout',
    badgeColor: 'taupe',
    title: 'Harmonisation des largeurs de page',
    items: [
      { type: '~', text: 'L\'affichage sur grand écran a été uniformisé sur l\'ensemble du site pour une expérience plus cohérente.', detail: 'Système de largeurs unifié sur 10 composants (56 remplacements) : max-w-[1280px] page · max-w-[1100px] grilles · max-w-[760px] prose · max-w-[600px] intros · max-w-[520px] texte court.' },
      { type: '~', text: 'Les pages Science, Praticiens, Soignants et Comment ça marche bénéficient désormais de ce nouveau système.', detail: 'Remplacement des classes max-w-3xl/4xl/5xl par les valeurs fixes du système unifié.' },
    ],
  },

  {
    version: 'v1.33.0',
    date: '22 mai 2026',
    badge: 'Tarifs & Navigation',
    badgeColor: 'lapis',
    title: 'Refonte tarifaire — 2 offres, 1 décision',
    items: [
      { type: '~', text: 'Page Tarifs entièrement refondée : 2 offres payantes uniquement (Mensuel 12,99 € / Annuel 59 €) avec toggle interactif. Annuel coché par défaut.' },
      { type: '-', text: 'Suppression des offres Semestriel (39 €/6 mois) et des niveaux gratuits de la grille principale — ils parasitaient la décision d\'achat.' },
      { type: '+', text: 'Fallback gratuit en bas de page : « Pas encore prêt ? Commence par l\'essai gratuit de 8 jours. » — séparé visuellement des offres payantes.' },
      { type: '+', text: 'Carte principale dynamique : prix, features et CTA s\'adaptent en temps réel au toggle Mensuel/Annuel.' },
      { type: '+', text: 'Section garanties (Paiement sécurisé · Annulation libre · Accès immédiat) ajoutée sous la carte.' },
      { type: '~', text: 'CTA navigation desktop : « Prendre rendez-vous » → « Essayer gratuitement » avec style lapis (bg-lapis).' },
      { type: '~', text: 'Badge PRO dans SiteNav et BlogFooter : bg-amber → bg-lapis pour alignement avec le design system v2.3.' },
      { type: '~', text: 'FAQ Tarifs mise à jour : 4 questions centrées sur l\'annulation, la différence Mensuel/Annuel, l\'essai gratuit et la protection des données.' },
    ],
  },
  {
    version: 'v1.32.5',
    date: '21 mai 2026',
    badge: 'Expansion B2B',
    badgeColor: 'lapis',
    title: 'Pages Praticiens & Soignants — Nouveaux canaux B2B',
    items: [
      { type: '+', text: 'Nouvelle page /fr/praticiens : positionnement psys/thérapeutes, programme partenaire (accès Pro offert + −30 % patients), formulaire en base de données.' },
      { type: '+', text: 'Nouvelle page /fr/soignants : positionnement professionnels de santé, offre pilote 3 mois gratuits pour équipe de 10, formulaire en base de données.' },
      { type: '+', text: 'Routes /fr/praticiens et /fr/soignants ajoutées dans App.tsx avec lazy loading.' },
      { type: '~', text: 'Menu « Plus » enrichi avec les liens Praticiens & Psys et Soignants.' },
    ],
  },
  {
    version: 'v1.32.4',
    date: '20 mai 2026',
    badge: 'Tarifs',
    badgeColor: 'amber',
    title: 'Nouveaux prix — 9 € / 39 € / 59 €',
    items: [
      { type: '~', text: 'Plan mensuel ajusté à 9 € (au lieu de 10 €).' },
      { type: '+', text: 'Nouveau plan Semestriel à 39 € / 6 mois avec badge « Économie 28 % ».' },
      { type: '~', text: 'Plan annuel ajusté à 59 € (au lieu de 60 €), avec badge « Meilleur prix » (économie de 45 % par rapport au mensuel).' },
      { type: '~', text: 'Mise à jour de la FAQ pour refléter les nouvelles économies réalisées sur 12 mois.' },
      { type: '+', text: 'Amélioration de la gestion des erreurs de paiement.', detail: 'Garde anti-TODO dans stripeService : message utilisateur si Price ID non configuré.' },
    ],
  },
  {
    version: 'v1.32.1',
    date: '20 mai 2026',
    badge: 'Affinage',
    badgeColor: 'taupe',
    title: 'Navigation, cartes de score et cadrage photo affinés',
    items: [
      { type: '~', text: 'Le menu « Plus » a été déplacé à droite de la barre de navigation pour un accès plus naturel.' },
      { type: '~', text: 'Le bouton d\'action principal sur ordinateur a été affiné visuellement.', detail: 'CTA desktop en style pill outline (bord ink, fond transparent).' },
      { type: '~', text: 'Les textes des 3 cartes de score ont été précisés : « Ancrage & clarté », « Tension active », « Disponibilité somatique ».' },
      { type: '~', text: 'L\'image d\'accueil a été recadrée et adoucie pour une meilleure intégration visuelle.', detail: 'Photo hero : cadrage amélioré (object-position center 20%), dégradé bas ajouté.' },
    ],
  },
  {
    version: 'v1.32.0',
    date: '20 mai 2026',
    badge: 'Design',
    badgeColor: 'amber',
    title: 'Nouvel accueil et navigation simplifiée',
    items: [
      { type: '~', text: 'La section d\'accueil a été entièrement repensée pour être plus claire et impactante.', detail: 'Layout split texte/photo, H1 Fraunces font-light, surtitre ambre « Régulation · Recentrage · Décision ».' },
      { type: '+', text: 'Ajout de 3 cartes de score illustratives pour visualiser le type de résultat obtenu avant de commencer.' },
      { type: '+', text: 'Les boutons ont été arrondis pour correspondre à notre nouvelle identité visuelle.', detail: 'Boutons pill (rounded-full) pour les CTAs principaux et secondaires.' },
      { type: '~', text: 'La navigation sur ordinateur a été simplifiée avec 3 liens principaux et un menu « Plus » regroupant les autres pages.' },
      { type: '~', text: 'Les arguments de réassurance ont été mis à jour : « Gratuit · Sans inscription · 3 minutes ».' },
    ],
  },
  {
    version: 'v1.31.0',
    date: '20 mai 2026',
    badge: 'Design System',
    badgeColor: 'lapis',
    title: 'Refonte de la page Changelog',
    items: [
      { type: '~', text: 'Page entièrement réécrite : composant ChangelogEntry réutilisable, style unifié pour toutes les entrées, fond bg-paper conforme au design system.' },
      { type: '~', text: 'Typographie corrigée : H1 en font-light (300) conforme au DS v2.2, accent en text-amber (plus text-terracotta).' },
      { type: '~', text: 'Tokens mis à jour : bg-cream → bg-paper, text-terracotta → text-amber, bg-terracotta → bg-amber dans toutes les entrées historiques.' },
      { type: '+', text: 'Détails techniques repliables : les noms de fichiers et fonctions internes sont masqués par défaut, accessibles via "Voir les détails techniques".' },
      { type: '+', text: 'CTA Lapis en bas de page pour inviter à faire l\'évaluation.' },
    ],
  },
  {
    version: 'v1.30.0',
    date: '20 mai 2026',
    badge: 'Monétisation',
    badgeColor: 'amber',
    title: 'Paiement Stripe — 3 plans disponibles',
    items: [
      { type: '+', text: 'Paiement sécurisé via Stripe Checkout : 3 plans au choix (10 € / 1 mois, 30 € / 6 mois, 60 € / 1 an). Aucune donnée bancaire ne transite par le site.' },
      { type: '+', text: 'Page de tarifs mise à jour avec les vrais boutons de paiement et un indicateur de chargement.' },
      { type: '+', text: 'Tableau de bord : bannière d\'invitation à débloquer 8 semaines de suivi complet.' },
      { type: '+', text: 'Pages de confirmation et d\'annulation après paiement.' },
    ],
    // détails techniques
  },
  {
    version: 'v1.29.1',
    date: '20 mai 2026',
    badge: 'Science',
    badgeColor: 'ventral',
    title: 'Méthode scientifique — corrections de rigueur',
    items: [
      { type: '~', text: 'La page Méthode précise désormais que l\'évaluation est un outil d\'auto-observation somatique, pas un diagnostic médical.' },
      { type: '~', text: 'Le lien entre sous-système passif et réponse de figement est formulé comme un modèle (Porges, 2011), pas une certitude anatomique.' },
      { type: '~', text: 'Le terme "Crise Allostatique" est contextualisé comme emprunt analogique à McEwen (1998).' },
      { type: '~', text: 'Le score /100 est décrit comme une estimation de disponibilité somatique perçue, pas une mesure instrumentale.' },
    ],
  },
  {
    version: 'v1.29.0',
    date: '20 mai 2026',
    badge: 'Science',
    badgeColor: 'ventral',
    title: 'Page Méthode — fondements scientifiques',
    items: [
      { type: '+', text: 'Nouvelle page /fr/methode : documentation des 3 sous-systèmes de Panjabi (1992), cartographie des 27 profils, calcul du score /100.' },
      { type: '+', text: 'Références académiques : Panjabi 1992, Porges 2011, Dana 2018, McEwen 1998, Siegel 1999.' },
    ],
  },
  {
    version: 'v1.28.0',
    date: '20 mai 2026',
    badge: 'Qualité',
    badgeColor: 'lapis',
    title: 'Navigation, domaine unifié et performance mobile',
    items: [
      { type: '~', text: 'Sélecteur de langue : EN affiche un badge "Bientôt" discret — plus de message d\'erreur. L\'infrastructure est prête pour une activation dans ~6 mois.' },
      { type: '~', text: 'Domaine unifié : toutes les références à l\'ancien nom ont été remplacées par "Amargi" et "amargi.me" sur l\'ensemble du site.' },
      { type: '~', text: 'Tableau de bord accessible depuis la navigation principale, même sans session préalable.' },
      { type: '~', text: 'Performance mobile améliorée : les modules lourds et les animations se chargent uniquement quand la page en a besoin.' },
    ],
  },
  {
    version: 'v1.27.0',
    date: '20 mai 2026',
    badge: 'Sprint 3 B+C',
    badgeColor: 'ventral',
    title: 'Navigation Dashboard et sécurité des données',
    items: [
      { type: '+', text: 'Lien "Mon suivi" dans la navigation principale — accès direct au tableau de bord depuis toutes les pages.' },
      { type: '+', text: 'Bouton tableau de bord ajouté sur la page de résultats.' },
      { type: '~', text: 'Boucle d\'enregistrement éliminée : le bouton "Voir mon tableau de bord" attend la confirmation de sauvegarde avant de devenir actif.' },
      { type: '+', text: 'Connexion anonyme sécurisée via Supabase Auth — les données de session sont liées à un identifiant unique, pas seulement au navigateur.' },
      { type: '~', text: 'Politiques RLS renforcées : seul l\'utilisateur propriétaire peut lire ses sessions.' },
    ],
  },
  {
    version: 'v1.26.0',
    date: '20 mai 2026',
    badge: 'Hotfix',
    badgeColor: 'crise',
    title: 'Correction du tableau de bord et des couleurs',
    items: [
      { type: '+', text: 'Nouveaux tokens de couleur pour les zones polyvagales (ventral/crise) — plus aucune couleur codée en dur dans le code source.' },
      { type: '~', text: 'Bug résolu : le tableau de bord affichait "Pas encore de données" même après une évaluation complète.' },
      { type: '~', text: 'Écran de synchronisation ajouté : si les données ne sont pas encore disponibles, un bouton "Actualiser" permet de les charger.' },
    ],
  },
  {
    version: 'v1.25.0',
    date: '20 mai 2026',
    badge: 'Sprint 2',
    badgeColor: 'lapis',
    title: 'Tableau de bord de suivi longitudinal',
    items: [
      { type: '+', text: 'Nouveau tableau de bord /fr/dashboard : 5 composants de suivi — série de jours consécutifs, résumé hebdomadaire, courbe de score, calendrier 8 jours, historique des profils.' },
      { type: '+', text: 'Squelette de chargement animé et écran "Pas encore de données" avec invitation à faire l\'évaluation.' },
      { type: '+', text: 'CTA "Voir mon tableau de bord" ajouté à l\'étape 6 du module d\'expérimentation.' },
    ],
  },
  {
    version: 'v1.24.0',
    date: '20 mai 2026',
    badge: 'Sprint 1',
    badgeColor: 'amber',
    title: 'Enregistrement des sessions et conformité RGPD',
    items: [
      { type: '+', text: 'Chaque évaluation est enregistrée de façon anonyme : identifiant local, historique des sessions, calcul du score moyen et du profil dominant.' },
      { type: '+', text: 'Case de consentement RGPD dans le formulaire email — le bouton reste désactivé tant que la case n\'est pas cochée.' },
      { type: '~', text: 'Politique de confidentialité mise à jour pour documenter l\'historique de sessions.' },
      { type: '~', text: 'Règles de base de données refondues pour sécuriser les nouvelles collections.' },
    ],
  },
  {
    version: 'v1.20.0',
    date: '19 mai 2026',
    badge: 'Parcours',
    badgeColor: 'amber',
    title: 'Redirection directe vers le module d\'expérimentation',
    items: [
      { type: '~', text: 'Après l\'évaluation, l\'utilisateur arrive directement sur le module d\'expérimentation — plus d\'étape intermédiaire.' },
      { type: '~', text: 'Le profil est transmis automatiquement au module sans saisie supplémentaire.' },
    ],
  },
  {
    version: 'v1.13.7',
    date: '15 mai 2026',
    badge: 'Accessibilité',
    badgeColor: 'lapis',
    title: 'Quiz — Contraste WCAG AA et palette Amargi',
    items: [
      { type: '~', text: 'Remplacement du dégradé vert→corail hors-charte par les tokens Amargi (sand→amber).' },
      { type: '~', text: 'Contraste WCAG AA garanti sur tous les boutons (ratio ≥ 4.5:1), hauteur minimum 44px pour l\'accessibilité tactile.' },
      { type: '+', text: 'Indicateur de focus visible et attribut aria-pressed sur chaque bouton pour les lecteurs d\'écran.' },
    ],
  },
  {
    version: 'v1.13.6',
    date: '15 mai 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Quiz — Grille de boutons 1–10',
    items: [
      { type: '~', text: 'Les sliders sont remplacés par une grille de 10 boutons colorés avec ancres sémantiques (min / modéré / max).' },
      { type: '+', text: 'Sélection unique par question avec retour visuel (agrandissement + ombre) et passage automatique après 1,2 seconde.' },
    ],
  },
  {
    version: 'v1.13.2',
    date: '15 mai 2026',
    badge: 'Contenu',
    badgeColor: 'taupe',
    title: 'Nouvelle page Science',
    items: [
      { type: '+', text: 'Page /fr/science créée : 4 piliers, 20 études, bibliothèque accordéon.' },
      { type: '+', text: 'Lien Science ajouté dans la navigation principale et le pied de page.' },
    ],
  },
  {
    version: 'v1.13.0',
    date: '14 mai 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Quiz — Lisibilité en 3 couches',
    items: [
      { type: '~', text: 'Badge "Exercice 1/2/3" remplace les labels techniques pour les 3 premières questions.' },
      { type: '+', text: 'Zone instruction sur fond ambre doux avec label "Faites le geste" — séparation visuelle claire entre instruction et question.' },
    ],
  },
  {
    version: 'v1.12.9',
    date: '14 mai 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: 'Quiz — L\'action avant la question',
    items: [
      { type: '~', text: 'Pour les 3 premières questions (Panjabi) : l\'instruction d\'action devient le titre principal, la question suit en sous-titre.' },
      { type: '~', text: 'Logique pédagogique : d\'abord le geste corporel, ensuite l\'auto-évaluation.' },
    ],
  },
  {
    version: 'v1.12.8',
    date: '14 mai 2026',
    badge: 'Fix UX',
    badgeColor: 'taupe',
    title: '3 corrections UX du quiz',
    items: [
      { type: '~', text: 'Slider personnalisé : le curseur est parfaitement aligné sur la piste, zone tactile 44px.' },
      { type: '~', text: 'Suppression du double texte contextuel — une seule source d\'information par question.' },
      { type: '~', text: 'Questions reformulées en phrases directes et mises en évidence.' },
    ],
  },
  {
    version: 'v1.12.7',
    date: '14 mai 2026',
    badge: 'UX',
    badgeColor: 'amber',
    title: '7 améliorations UX du quiz',
    items: [
      { type: '~', text: 'Badges fonctionnels (Corps, Articulations, Mental, Sommeil, Stress, Énergie) — suppression du jargon technique.' },
      { type: '+', text: 'Slider sans valeur par défaut : affiche "?" jusqu\'au premier contact.' },
      { type: '+', text: 'Retour visuel après interaction : icône ✓ + "Noté" en ambre pendant 1,2 seconde.' },
      { type: '~', text: 'Délai de passage automatique augmenté de 0,6 s à 1,2 s pour permettre un changement d\'avis.' },
      { type: '+', text: 'Écran de transition 1,5 s entre le bloc Panjabi (Q1–Q3) et le bloc clinique (Q4–Q6).' },
    ],
  },
  {
    version: 'v1.12.6',
    date: '14 mai 2026',
    badge: 'Quiz',
    badgeColor: 'amber',
    title: 'Quiz 6 questions — auto-avance et progression animée',
    items: [
      { type: '~', text: 'Quiz ramené à 6 questions : 3 Panjabi (Actif, Passif, Neural) + 3 cliniques (Sommeil, Stress, Disponibilité).' },
      { type: '+', text: 'Passage automatique après interaction : 600 ms pour les sliders, 300 ms pour les cartes.' },
      { type: '+', text: 'Barre de progression ambre avec lueur animée. Indicateur d\'étapes : cercles qui s\'allongent sur la question active.' },
    ],
  },
  {
    version: 'v1.12.5',
    date: '14 mai 2026',
    badge: 'Quiz',
    badgeColor: 'amber',
    title: 'Nouveau quiz 90 secondes',
    items: [
      { type: '+', text: 'Quiz entièrement réécrit : 6 questions couvrant les 3 sous-systèmes de Panjabi et 3 questions cliniques de contexte.' },
      { type: '+', text: 'Algorithme Panjabi × Polyvagal : calcul des 27 profils (L/M/H sur 3 axes).' },
      { type: '+', text: 'Transitions slide horizontales, barre de progression ambre, passage automatique.' },
    ],
  },
  {
    version: 'v1.12.3',
    date: '14 mai 2026',
    badge: 'Fix',
    badgeColor: 'taupe',
    title: 'Corrections navigation et Hero',
    items: [
      { type: '~', text: 'Logo du header cliquable — retour à la page d\'accueil corrigé.' },
      { type: '~', text: 'Hero : logo agrandi, espacement resserré, double CTA ambre.' },
      { type: '~', text: 'Pied de page : liens alignés sur la navigation principale.' },
    ],
  },
  {
    version: 'v1.12.2',
    date: '14 mai 2026',
    badge: 'Navigation',
    badgeColor: 'lapis',
    title: 'Navigation principale et pied de page restructurés',
    items: [
      { type: '+', text: 'Composant SiteNav partagé : logo, liens centrés, badge Pro, CTA "Essayer gratuitement".' },
      { type: '+', text: 'Menu hamburger responsive sur mobile avec overlay complet.' },
      { type: '+', text: 'Pied de page restructuré en 4 colonnes : Marque / Navigation / Ressources / CTA.' },
    ],
  },
  {
    version: 'v1.12.1',
    date: '14 mai 2026',
    badge: 'Assets',
    badgeColor: 'taupe',
    title: 'Logos et favicon Amargi mis à jour',
    items: [
      { type: '~', text: 'Remplacement de tous les assets logo/favicon par les versions officielles finales.' },
      { type: '+', text: 'Composant AmargiLogo : 8 variantes (primary, horizontal, reversed, mono-dark, mono-light, lockup-halo, icon, social).' },
      { type: '+', text: 'Favicon multi-format : SVG + PNG 32px + PNG 48px + Apple Touch Icon.' },
    ],
  },
  {
    version: 'v1.12.0',
    date: '14 mai 2026',
    badge: 'Design System',
    badgeColor: 'lapis',
    title: 'Design System Amargi — Refonte visuelle complète',
    items: [
      { type: '+', text: 'Polices officielles : Fraunces (serif) + Inter (sans) servies en local.' },
      { type: '+', text: 'Palette de tokens : Sepia Ink, Paper-Oat, Organic Amber, Warm Sand, Lapis.' },
      { type: '~', text: '488 lignes de couleurs codées en dur remplacées par les tokens Tailwind sur 17 fichiers.' },
    ],
  },
  {
    version: 'v1.11.0',
    date: '14 mai 2026',
    badge: 'Feature',
    badgeColor: 'amber',
    title: 'Nouvelle page d\'accueil Amargi — 8 sections',
    items: [
      { type: '+', text: 'Refonte complète de la page d\'accueil : Hero, Le Problème, Intelligence Somatique, 4 Piliers, Philosophie, Preuves, Quick-Start, CTA final.' },
      { type: '+', text: 'Halo ambre SVG intégré dans le Hero.' },
    ],
  },
  {
    version: 'v1.7.0',
    date: '13 mai 2026',
    badge: 'Contenu',
    badgeColor: 'taupe',
    title: 'Module d\'expérimentation — 6 étapes',
    items: [
      { type: '+', text: 'Module d\'expérimentation en 6 étapes : résultat, exercice personnalisé, respiration, ancrage, intégration, prochaines étapes.' },
      { type: '+', text: 'Exercices adaptés aux 27 profils polyvagaux.' },
    ],
  },
  {
    version: 'v1.6.0',
    date: '6 mai 2026',
    badge: 'SEO',
    badgeColor: 'taupe',
    title: 'Arborescence SEO et versioning global',
    items: [
      { type: '~', text: 'Sitemap et robots.txt mis à jour pour mapper la structure de routage FR/EN finale.' },
      { type: '~', text: 'Versioning unifié sur tous les composants.' },
    ],
  },
  {
    version: 'v1.5.5',
    date: '27 avril 2026',
    badge: 'SEO',
    badgeColor: 'taupe',
    title: 'Open Graph, partage et Journal',
    items: [
      { type: '+', text: 'Balises Open Graph et Twitter Card ajoutées sur toutes les pages avec image de couverture par défaut.' },
      { type: '~', text: 'Journal : intégration d\'images pour les articles, module CTA de conversion, corrections des traductions de dates.' },
    ],
  },
  {
    version: 'v1.5.4',
    date: '24 avril 2026',
    badge: 'SEO',
    badgeColor: 'taupe',
    title: 'SEO international dynamique',
    items: [
      { type: '+', text: 'Dictionnaire SEO bilingue : balises title, og:title et description traduites dynamiquement sur toute l\'application.' },
      { type: '~', text: 'Correction du build SSG pour les pages avec appels asynchrones aux modules de traduction.' },
    ],
  },
  {
    version: 'v1.5.3',
    date: '21 avril 2026',
    badge: 'SEO',
    badgeColor: 'taupe',
    title: 'Architecture SEO bilingue stricte',
    items: [
      { type: '+', text: 'Composant SEO global : injection automatique de lang, canonicals et hreflang — plus de duplication de contenu pour Google.' },
      { type: '+', text: 'Robots.txt et sitemap.xml structuré avec priorités de crawl.' },
    ],
  },
  {
    version: 'v1.5.2',
    date: '21 avril 2026',
    badge: 'Performance',
    badgeColor: 'taupe',
    title: 'Optimisation mobile et accessibilité',
    items: [
      { type: '~', text: 'Toutes les routes passées en import dynamique — la page d\'accueil allège son poids mort de plus de 340 Ko.' },
      { type: '~', text: 'Contraste typographique renforcé : ratio minimum 5.5:1 sur tous les textes secondaires.' },
    ],
  },
  {
    version: 'v1.5.1',
    date: '21 avril 2026',
    badge: 'Performance',
    badgeColor: 'taupe',
    title: 'Polices locales et bundle splitting',
    items: [
      { type: '~', text: 'Suppression de Google Fonts : Fraunces et Inter servies en local via NPM — plus de dépendance DNS externe.' },
      { type: '+', text: 'Isolation des packages lourds (Auth, PDF) en chunks séparés — chargement uniquement à la demande.' },
      { type: '+', text: 'Repère sémantique <main> ajouté pour stabiliser la navigation assistée (VoiceOver, NVDA).' },
    ],
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Changelog() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-paper text-ink font-sans">
      <SEO
        title={t('seo.changelog.title')}
        description={t('seo.changelog.desc')}
        path="/mises-a-jour"
      />

      <SiteNav logoVariant="primary" />

      <main className="pt-24 pb-20 md:pt-36 md:pb-32 px-5 md:px-8 max-w-[760px] mx-auto">

        {/* En-tête de page */}
        <header className="mb-16">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber mb-4">
            Amargi · Journal des modifications
          </p>
          <h1 className="font-serif text-[36px] md:text-[52px] font-light text-ink leading-[1.1] tracking-[-0.02em] mb-5">
            Historique des <span className="text-amber italic">versions</span>
          </h1>
          <p className="text-[16px] text-taupe leading-relaxed max-w-[520px]">
            Chaque version documente les améliorations apportées à l'outil, à la méthode et à l'expérience utilisateur.
          </p>
        </header>

        {/* Liste des entrées */}
        <div className="space-y-10">
          {ENTRIES.map((entry) => (
            <ChangelogEntry key={entry.version} {...entry} />
          ))}
        </div>

        {/* CTA final */}
        <section className="mt-20 pt-12 border-t border-sand/60">
          <p className="font-serif text-[22px] md:text-[28px] font-light text-ink mb-3 leading-snug">
            Prêt à évaluer votre système nerveux ?
          </p>
          <p className="text-[14px] text-taupe mb-7 leading-relaxed">
            L'évaluation prend moins de 90 secondes. Vous obtenez votre profil et un exercice personnalisé immédiatement.
          </p>
          <AppLink
            to="/fr/evaluation"
            className="inline-flex items-center gap-2 bg-lapis text-paper px-6 py-3 rounded-lg text-[14px] font-semibold hover:bg-lapis-deep transition-colors"
          >
            Faire mon évaluation gratuite
            <ArrowRight size={15} />
          </AppLink>
        </section>

      </main>

      <BlogFooter />
    </div>
  );
}
